import React, { useState, useRef, useEffect } from 'react';
import type { DataBasis } from '../utils/dataBasis';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { serializeForm, type AutosaveState } from '../utils/formSerialize';
import RichTextEditor from './RichTextEditor';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { DefinitionHover, GlossaryDrawer } from './Definitions';
import { formatDistanceToNow } from 'date-fns';
import {
  CaseFormData,
  CaseFile,
  ViolationDetail,
  createEmptyViolationDetail,
  SUGGESTED_CASE_TAGS,
  PERPETRATOR_TYPE_GROUPS,
  OTHER_VIOLATION_TYPES,
  VICTIM_GROUP_TYPES, INDIGENOUS_GROUP_TYPE_VALUES, NON_INDIGENOUS_GROUP_TYPE_VALUES,
  COMMUNITY_ROLE_TYPES,
  ACCUSATION_OUTCOMES,
  DETENTION_TYPES,
  DUE_PROCESS_VIOLATIONS,
  IMPACT_TYPES,
  CRIMINALIZATION_TYPES,
  CRIMINALIZATION_TRIGGERS,
  LEGAL_INSTRUMENT_TYPES,
  PROCEEDING_TYPES,
  PERPETRATOR_ACCOUNTABILITY_STATUSES,
  DEVELOPMENT_PROJECT_TYPES, emptyDevelopmentProject, DevelopmentProjectForm,
  AGE_BRACKETS,
  DEPENDENT_TYPES,
  STEP_LABELS,
  getViolationTypeLabel,
  getViolationColor,
  NEUTRAL_BADGE,
  PERPETRATOR_TYPES,
} from '../types/caseForm';
import FileUpload from './FileUpload';
import AiFieldMark from './AiFieldMark';
import { VIOLATION_TYPES_TAXONOMY, VIOLATION_PAIRINGS, IMPACT_TYPES_TAXONOMY, taxonomyByKey, describeValue, defineValue, developmentProjectSubtypesFor, deriveDevelopmentProject } from '../types/taxonomy';
import LocationPicker from './LocationPicker';
import TagInput from './TagInput';
import ComboSelect from './ComboSelect';
import useCountries from '../hooks/useCountries';
import { enumLabel } from '../utils/enumLabel';

interface CaseFormWizardProps {
  formData: CaseFormData;
  setFormData: React.Dispatch<React.SetStateAction<CaseFormData>>;
  onSubmit: (asDraft: boolean, bypassValidation?: boolean) => void;
  loading: boolean;
  isEditing?: boolean;
  caseStatus?: string;
  // Let the user save a case that fails validation, after confirming a dialog that lists what
  // failed. Set for ADMINs on the edit screen; the backend waives its own checks for the same
  // people, but only when this flow forwards the explicit opt-in.
  allowValidationBypass?: boolean;
  // Where Cancel goes (the case page on Edit, the list on Create).
  cancelTo?: string;
  // Gathered by the automated pipeline: no human documenter, so name/organization are optional.
  isAutoSourced?: boolean;
  // Draft autosave, owned by the page: the wizard only shows its state.
  autosave?: AutosaveState;
  // Serialised form as last saved by autosave; when set, "unsaved changes" means "differs from this".
  cleanSnapshot?: string | null;
  // Called when the user confirms leaving via Cancel (Create uses it to drop the local draft).
  onDiscard?: () => void;
  // AI provenance (Edit only): the case's metadata.ai, its id, and a refresh after Confirm/Undo.
  aiMeta?: { autoApplied?: any[] } | null;
  caseId?: string;
  onAiChange?: () => void;
  // For EditCase document management
  existingDocuments?: React.ReactNode;
  // Open on this step (1-based). Used by "add" links from the case detail page.
  initialStep?: number;
}

const TOTAL_STEPS = 9;

// Violations that commonly accompany an FPIC violation. NOT a requirement — selecting FPIC_VIOLATION
// without any of these is valid and submits fine. They only drive the two-way reminders in step 5.
const FPIC_RELATED_TYPES: string[] = ['FORCED_DISPLACEMENT', 'LAND_GRABBING', 'THREATS_AND_INTIMIDATION'];

const getViolationTypeLabelSafe = (t: string) => enumLabel(t);

const CaseFormWizard: React.FC<CaseFormWizardProps> = ({
  formData,
  setFormData,
  onSubmit,
  loading,
  isEditing = false,
  caseStatus,
  allowValidationBypass = false,
  cancelTo = '/cases',
  isAutoSourced = false,
  autosave,
  cleanSnapshot,
  onDiscard,
  aiMeta,
  caseId,
  onAiChange,
  existingDocuments,
  initialStep,
}) => {
  const isDraftEditable = !isEditing || caseStatus === 'DRAFT' || !caseStatus;
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { countries } = useCountries();
  // Step lives in the URL (?step=N) so links can open a screen and the back button works; ?field=
  // names a field to open on arrival (used by the case page's "not documented" links).
  const [searchParams, setSearchParams] = useSearchParams();
  const clampStep = (n: number) => Math.min(TOTAL_STEPS, Math.max(1, Math.floor(n)));
  const [currentStep, setCurrentStep] = useState(() => clampStep(initialStep ?? (Number(searchParams.get('step')) || 1)));
  const [arrivalField] = useState<string | null>(() => searchParams.get('field'));
  const [visited, setVisited] = useState<Set<number>>(() => new Set([clampStep(initialStep ?? (Number(searchParams.get('step')) || 1))]));
  useEffect(() => {
    setVisited(prev => (prev.has(currentStep) ? prev : new Set(prev).add(currentStep)));
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (String(currentStep) === next.get('step')) return prev;
      next.set('step', String(currentStep));
      next.delete('field');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  useEffect(() => {
    const n = Number(searchParams.get('step'));
    if (n && clampStep(n) !== currentStep) setCurrentStep(clampStep(n));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const navigate = useNavigate();

  // Unsaved-changes guard: compare against the form as it was when the wizard mounted (Edit
  // mounts after the case has loaded; Create starts with the documenter prefilled).
  const initialSnapshot = useRef(serializeForm(formData));
  const dirty = !loading && (serializeForm(formData) !== (cleanSnapshot ?? initialSnapshot.current) || formData.files.length > 0);
  const confirmLeave = () => confirm({
    title: 'Leave without saving?',
    message: 'You have unsaved changes on this case. If you leave now they will be lost.',
    confirmLabel: 'Leave and discard',
    cancelLabel: 'Stay',
    variant: 'danger',
  });
  useUnsavedChanges(dirty, confirmLeave);
  const handleCancel = async () => {
    if (dirty && !(await confirmLeave())) return;
    onDiscard?.();
    navigate(cancelTo);
  };
  // Keeps "saved 12 s ago" moving while nothing else re-renders.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!autosave?.savedAt) return;
    const t = setInterval(() => tick(n => n + 1), 20000);
    return () => clearInterval(t);
  }, [autosave?.savedAt]);
  const renderAutosave = () => {
    if (!autosave || autosave.status === 'idle') return null;
    const where = autosave.where === 'server' ? 'Draft saved' : 'Draft kept in this browser';
    if (autosave.status === 'saving') return <span className="text-xs text-gray-500 dark:text-gray-400">Saving…</span>;
    if (autosave.status === 'error') return (
      <span className="text-xs text-red-700 dark:text-red-300">
        Couldn't save the draft.{' '}
        {autosave.onRetry && <button type="button" onClick={autosave.onRetry} className="underline">Retry</button>}
      </span>
    );
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400" title={autosave.savedAt ? new Date(autosave.savedAt).toLocaleString() : undefined}>
        {where}{autosave.savedAt ? ` · ${formatDistanceToNow(autosave.savedAt, { addSuffix: true })}` : ''}
      </span>
    );
  };
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [editingLocation, setEditingLocation] = useState(() => ['country', 'region', 'city', 'specificLocation', 'indigenousTerritory', 'latitude', 'longitude'].includes(arrivalField || ''));

  const updateFormData = (field: keyof CaseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (stepErrors[field]) {
      setStepErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // What a deselect would throw away: the violation's own detail record (if anything was typed
  // into it) and every victim's gender/age breakdown for it. Named so the dialog can say it.
  const detailHasContent = (vd: any) => {
    if (!vd) return false;
    const empty: any = createEmptyViolationDetail(vd.violationType);
    return Object.keys(vd).some(k => k !== 'violationType' && JSON.stringify(vd[k] ?? null) !== JSON.stringify(empty[k] ?? null));
  };
  const victimsWithBreakdown = (key: string) => formData.victims.filter(v => {
    const d = (v.violationDemographics || {})[key];
    return d && Object.values(d).some(x => x !== null && x !== undefined && x !== '');
  }).length;
  const confirmViolationRemoval = async (label: string, detailKey: string | null, tagKey: string) => {
    const losses: string[] = [];
    if (detailKey && detailHasContent(formData.violationDetails.find(vd => vd.violationType === detailKey))) {
      losses.push(`the details entered for it (perpetrators, legal case, state action and the rest)`);
    }
    const n = victimsWithBreakdown(tagKey);
    if (n > 0) losses.push(`the gender and age breakdown recorded for it on ${n} victim${n === 1 ? '' : 's'}`);
    if (losses.length === 0) return true;
    return confirm({
      title: `Remove ${label}?`,
      message: `Unselecting it deletes ${losses.join(' and ')}. Selecting it again later starts from empty.`,
      confirmLabel: 'Remove and delete',
      cancelLabel: 'Keep it',
      variant: 'danger',
    });
  };

  const handleViolationTypeChange = async (type: string, checked: boolean) => {
    if (!checked && !(await confirmViolationRemoval(getViolationTypeLabelSafe(type), type, type))) return;
    setFormData(prev => ({
      ...prev,
      violationTypes: checked
        ? [...prev.violationTypes, type]
        : prev.violationTypes.filter(t => t !== type),
      violationDetails: checked
        ? [...prev.violationDetails, createEmptyViolationDetail(type)]
        : prev.violationDetails.filter(vd => vd.violationType !== type),
      // Adding a violation selects it on every victim by default; removing it prunes it.
      victims: checked
        ? prev.victims.map(v => {
            const tags = v.violationTags || [];
            return { ...v, violationTags: tags.includes(type) ? tags : [...tags, type] };
          })
        : prev.victims.map(v => ({ ...v, violationTags: (v.violationTags || []).filter(t => t !== type) }))
    }));
    if (stepErrors.violationTypes) {
      setStepErrors(prev => {
        const next = { ...prev };
        delete next.violationTypes;
        return next;
      });
    }
  };

  // All violation keys the case currently has selected (KILLING/CRIMINALIZATION + other types).
  const violationKeysOf = (fd: any): string[] => {
    const keys: string[] = [];
    if (fd.violationTypes.includes('KILLING')) keys.push('KILLING');
    if (fd.violationTypes.includes('CRIMINALIZATION')) keys.push('CRIMINALIZATION');
    (fd.otherViolationTypes || []).forEach((o: string) => keys.push(o));
    return keys;
  };

  const addVictim = () => {
    setFormData(prev => ({
      ...prev,
      // Default: a new victim is tagged with all of the case's violations; deselect as needed.
      victims: [...prev.victims, { isGroup: false, violationTags: violationKeysOf(prev) }]
    }));
  };

  const removeVictim = (index: number) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.filter((_, i) => i !== index)
    }));
  };

  // A case holds at most one Indigenous group victim, and any number of non-Indigenous groups
  // (lawyers, protesters, a support organisation …) targeted alongside it. Mirrors
  // coalesceGroupVictims on the backend.
  const isGroupOf = (v: { isGroup: boolean; isIndigenous?: boolean }, indigenous: boolean) =>
    v.isGroup && (v.isIndigenous !== false) === indigenous;

  const addGroupVictim = (indigenous = true) => {
    setFormData(prev => indigenous && prev.victims.some(v => isGroupOf(v, true))
      ? prev
      : { ...prev, victims: [...prev.victims, indigenous
          ? { isGroup: true, groupName: prev.indigenousGroup, violationTags: violationKeysOf(prev) }
          : { isGroup: true, isIndigenous: false, groupName: [], violationTags: violationKeysOf(prev) }] });
  };

  const removeGroupVictim = () => {
    setFormData(prev => ({ ...prev, victims: prev.victims.filter(v => !isGroupOf(v, true)) }));
  };

  const updateVictim = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) =>
        i === index ? { ...victim, [field]: value } : victim
      )
    }));
  };

  // Toggle a violation tag for one victim (used by the victim x violation grid in step 5).
  const toggleVictimViolation = async (index: number, key: string) => {
    // Turning a tag off for a victim drops that victim's breakdown for it — ask first if there is one.
    const victim = formData.victims[index];
    const tagsNow = Array.isArray(victim?.violationTags) ? victim.violationTags : [];
    if (tagsNow.includes(key)) {
      const d = (victim.violationDemographics || {})[key];
      const hasBreakdown = d && Object.values(d).some(x => x !== null && x !== undefined && x !== '');
      if (hasBreakdown) {
        const ok = await confirm({
          title: `Remove ${getViolationTypeLabelSafe(key)} from this victim?`,
          message: 'The counts recorded for it on this victim will be deleted.',
          confirmLabel: 'Remove and delete',
          cancelLabel: 'Keep it',
          variant: 'danger',
        });
        if (!ok) return;
      }
    }
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const tags = Array.isArray(victim.violationTags) ? victim.violationTags : [];
        const isOn = tags.includes(key);
        const demo = { ...(victim.violationDemographics || {}) };
        if (isOn) {
          delete demo[key];
        } else if (victim.isGroup && demo[key] == null) {
          // Default a group's per-violation total to its overall affected-individuals, if entered.
          const dflt = (victim as any).affectedIndividualsCount;
          demo[key] = (dflt != null && dflt !== '')
            ? { affectedCount: typeof dflt === 'number' ? dflt : parseInt(dflt, 10) }
            : {};
        }
        return {
          ...victim,
          violationTags: isOn ? tags.filter(t => t !== key) : [...tags, key],
          violationDemographics: demo,
        };
      }),
    }));
  };

  // Per-violation count for a GROUP victim (unnamed people affected by that violation).
  // Set one field (affectedCount or a demographic count) of one violation for a group victim.
  const setVictimViolationField = (index: number, key: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const demo = { ...(victim.violationDemographics || {}) };
        const entry: any = { ...(demo[key] || {}) };
        if (value === '' || value == null) delete entry[field];
        else entry[field] = parseInt(value, 10);
        demo[key] = entry;
        return { ...victim, violationDemographics: demo };
      }),
    }));
  };

  // Set a non-numeric per-violation field (outcome enums / arrays) of one violation
  // for a victim. Empty string / empty array clears the field.
  const setVictimViolationMeta = (index: number, key: string, field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const demo = { ...(victim.violationDemographics || {}) };
        const entry: any = { ...(demo[key] || {}) };
        if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) delete entry[field];
        else entry[field] = value;
        demo[key] = entry;
        return { ...victim, violationDemographics: demo };
      }),
    }));
  };

  // Copy one violation's whole breakdown onto another (a starting point the user then edits).
  const copyVictimViolationDemographics = (index: number, fromKey: string, toKey: string) => {
    if (!fromKey || fromKey === toKey) return;
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const demo = { ...(victim.violationDemographics || {}) };
        demo[toKey] = { ...(demo[fromKey] || {}) };
        return { ...victim, violationDemographics: demo };
      }),
    }));
  };

  // The violation "keys" the case is tagged with — used for the per-victim pills.
  const selectedViolationOptions = (): { key: string; label: string }[] => {
    const opts: { key: string; label: string }[] = [];
    if (formData.violationTypes.includes('KILLING')) opts.push({ key: 'KILLING', label: 'Killing' });
    if (formData.violationTypes.includes('CRIMINALIZATION')) opts.push({ key: 'CRIMINALIZATION', label: 'Criminalization' });
    formData.otherViolationTypes.forEach(o => {
      const opt = OTHER_VIOLATION_TYPES.find(x => x.value === o);
      opts.push({ key: o, label: opt ? opt.label : o });
    });
    return opts;
  };

  // A row of toggle pills letting one victim pick which of the case's violations affected them.
  const GENDER_FIELDS = [
    { f: 'affectedMaleCount', l: 'Male' },
    { f: 'affectedFemaleCount', l: 'Female' },
  ];
  const AGE_FIELDS = [
    { f: 'affected0to12Count', l: '0–12' },
    { f: 'affected13to25Count', l: '13–25' },
    { f: 'affected26to35Count', l: '26–35' },
    { f: 'affected36to59Count', l: '36–59' },
    { f: 'affected60PlusCount', l: '60+' },
  ];

  const renderVictimViolationPills = (index: number) => {
    const victim = formData.victims[index];
    const options = selectedViolationOptions();
    const isGroup = !!victim?.isGroup;
    const isSelected = (key: string) => Array.isArray(victim?.violationTags) && victim.violationTags.includes(key);
    const demoOf = (key: string): any => (victim as any)?.violationDemographics?.[key] || {};
    const groupTotalRaw = (victim as any)?.affectedIndividualsCount;
    const groupTotalNum = Number(groupTotalRaw);
    const haveGroupTotal = groupTotalRaw != null && groupTotalRaw !== '' && !Number.isNaN(groupTotalNum) && groupTotalNum > 0;
    const hasAnyBreakdown = options.some(o => {
      if (!isSelected(o.key)) return false;
      const d = demoOf(o.key);
      return Object.keys(d).some(k => d[k] != null && d[k] !== '');
    });
    const overGroupWarning = (key: string) => {
      if (!haveGroupTotal) return null;
      const c = demoOf(key).affectedCount;
      if (c == null || c === '') return null;
      if (Number(c) > groupTotalNum) {
        return <p className="text-[11px] text-amber-600 mt-1.5">Exceeds the group total of {groupTotalNum} affected — check for a typo.</p>;
      }
      return null;
    };
    const sumHint = (key: string, fields: string[]) => {
      const d = demoOf(key);
      const present = fields.filter(f => d[f] != null);
      if (present.length === 0) return <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">not disaggregated</span>;
      const sum = present.reduce((a: number, f: string) => a + Number(d[f]), 0);
      const total = d.affectedCount;
      if (total == null) return <span className="text-[11px] text-gray-400 dark:text-gray-500">{sum} entered</span>;
      if (sum === Number(total)) return <span className="text-[11px] text-emerald-600">✓ {sum} of {total}</span>;
      if (sum < Number(total)) return <span className="text-[11px] text-gray-500 dark:text-gray-400">{sum} of {total} · {Number(total) - sum} unspecified</span>;
      return <span className="text-[11px] text-amber-600">{sum} of {total} · over total</span>;
    };
    const numCls = 'block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm text-sm text-center px-1 focus:border-emerald-500 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
    // Per-person detention/proceeding outcomes on a CRIMINALIZATION or ARBITRARY_DETENTION row
    // (named individuals; group rows use the counts).
    const renderOutcomeFields = (key: string) => {
      const d = demoOf(key);
      const dpv: string[] = Array.isArray(d.dueProcessViolations) ? d.dueProcessViolations : [];
      const selCls = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm text-sm focus:border-emerald-500 focus:ring-emerald-500';
      return (
        <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{key === 'CRIMINALIZATION' ? 'Criminalization' : 'Arbitrary detention'} outcome for this victim</span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="outcome-of-accusations" className="block text-[11px] text-gray-500 dark:text-gray-400">Outcome of accusations</label>
              <select id="outcome-of-accusations" value={(d.accusationOutcome as string) || ''} onChange={(e) => setVictimViolationMeta(index, key, 'accusationOutcome', e.target.value)} className={selCls}>
                <option value="">No information</option>
                {sortOptions(ACCUSATION_OUTCOMES).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
              {selectedDef('accusationOutcome', (d.accusationOutcome as string) || '')}
            </div>
            <div>
              <label htmlFor="detention" className="block text-[11px] text-gray-500 dark:text-gray-400">Detention</label>
              <select id="detention" value={(d.detentionType as string) || ''} onChange={(e) => setVictimViolationMeta(index, key, 'detentionType', e.target.value)} className={selCls}>
                <option value="">Not detained / no information</option>
                {sortOptions(DETENTION_TYPES).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
              {selectedDef('detentionType', (d.detentionType as string) || '')}
            </div>
            <div>
              <label htmlFor={`days-in-detention-${index}-${key}`} className="block text-[11px] text-gray-500 dark:text-gray-400">Days in detention</label>
              <input id={`days-in-detention-${index}-${key}`} type="number" min="0" value={d.detentionDays ?? ''} onChange={(e) => setVictimViolationField(index, key, 'detentionDays', e.target.value)} className={selCls} />
            </div>
          </div>
          <div className="mt-3">
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">Due-process violations experienced · {listRule('dueProcessViolations')}</span>
            <div className="flex flex-wrap gap-1.5">
              {sortOptions(DUE_PROCESS_VIOLATIONS).map(opt => {
                const on = dpv.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={on}
                    title={describeValue('dueProcessViolations', opt.value)}
                    onClick={() => setVictimViolationMeta(index, key, 'dueProcessViolations', on ? dpv.filter(v => v !== opt.value) : [...dpv, opt.value])}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-orange-600 text-white border-orange-600' : NEUTRAL_BADGE}`}
                  >
                    {on ? '✓ ' : ''}{opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    };
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Violation(s) this victim was subjected to *</label>
        {options.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">Select the case's violation categories in the previous step first.</p>
        ) : isGroup ? (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">Tap to deselect any that didn't apply. For each that did, enter the total affected, and — only where you actually know it — the gender/age split. Leave a field blank when it isn't known; blank means "not recorded", not zero. Count only the group's unnamed members.</p>
          {!haveGroupTotal && hasAnyBreakdown && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-2.5 py-1.5 mb-2">
              Group total not recorded — that’s fine if it’s unknown. If you know it, add it under “Number of Affected People” above to cross-check these per-violation counts.
            </p>
          )}
          <div className="space-y-2">
            {options.map(o => {
              const selected = isSelected(o.key);
              const others = options.filter(x => x.key !== o.key && isSelected(x.key));
              return (
                <div key={o.key} className={`rounded-lg border ${selected ? 'border-gray-200 dark:border-gray-700 p-3' : 'border-transparent p-1'}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleVictimViolation(index, o.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors min-w-[10rem] text-center ${
                        selected ? getViolationColor(o.key).solid : NEUTRAL_BADGE
                      }`}
                    >
                      {selected ? '✓ ' : ''}{o.label}
                    </button>
                    {selected && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 whitespace-nowrap">
                          Total affected
                          <input
                            type="number"
                            min="0"
                            value={demoOf(o.key).affectedCount ?? ''}
                            onChange={(e) => setVictimViolationField(index, o.key, 'affectedCount', e.target.value)}
                            placeholder="count"
                            className="w-20 text-center rounded-md border-gray-300 dark:border-gray-600 shadow-sm text-sm focus:border-emerald-500 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </label>
                        {others.length > 0 && (
                          <select
                            value=""
                            onChange={(e) => copyVictimViolationDemographics(index, e.target.value, o.key)}
                            className="text-xs rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 py-1.5"
                          >
                            <option value="">Copy from…</option>
                            {others.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                  {selected && overGroupWarning(o.key)}
                  {selected && (
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3">
                      <div>
                        <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Gender</span>
                        <div className="flex gap-1.5">
                          {GENDER_FIELDS.map(g => (
                            <div key={g.f} className="w-16">
                              <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 text-center">{g.l}</label>
                              <input
                                type="number" min="0"
                                value={demoOf(o.key)[g.f] ?? ''}
                                onChange={(e) => setVictimViolationField(index, o.key, g.f, e.target.value)}
                                className={numCls}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-1">{sumHint(o.key, GENDER_FIELDS.map(g => g.f))}</div>
                        {fieldError(`victims[${index}].demo.${o.key}.gender`)}
                      </div>
                      <div>
                        <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Age</span>
                        <div className="flex gap-1.5">
                          {AGE_FIELDS.map(a => (
                            <div key={a.f} className="w-14">
                              <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 text-center">{a.l}</label>
                              <input
                                type="number" min="0"
                                value={demoOf(o.key)[a.f] ?? ''}
                                onChange={(e) => setVictimViolationField(index, o.key, a.f, e.target.value)}
                                className={numCls}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-1">{sumHint(o.key, AGE_FIELDS.map(a => a.f))}</div>
                        {fieldError(`victims[${index}].demo.${o.key}.age`)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">Tap to deselect any that didn't apply to this victim.</p>
          <div className="flex flex-wrap gap-2">
            {options.map(o => {
              const selected = isSelected(o.key);
              return (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleVictimViolation(index, o.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected ? getViolationColor(o.key).solid : NEUTRAL_BADGE
                  }`}
                >
                  {selected ? '✓ ' : ''}{o.label}
                </button>
              );
            })}
          </div>
          {['CRIMINALIZATION', 'ARBITRARY_DETENTION'].filter(isSelected).map(k => (
            <React.Fragment key={k}>{renderOutcomeFields(k)}</React.Fragment>
          ))}
          </>
        )}
      </div>
    );
  };

  // Development projects: several per case, the first is the primary one.
  const addProject = () => setFormData(prev => ({ ...prev, developmentProjects: [...prev.developmentProjects, emptyDevelopmentProject()] }));
  const removeProject = (index: number) => setFormData(prev => ({ ...prev, developmentProjects: prev.developmentProjects.filter((_, i) => i !== index) }));
  // A patch on one row. Changing the type keeps only the subtypes the new type allows; ticking a
  // transition mineral moves the type to Transition mineral mining — the same rules the API applies.
  const updateProject = (index: number, patch: Partial<DevelopmentProjectForm>) => setFormData(prev => ({
    ...prev,
    developmentProjects: prev.developmentProjects.map((p, i) => {
      if (i !== index) return p;
      const next = { ...p, ...patch };
      return (patch.type !== undefined || patch.subtypes !== undefined) ? { ...next, ...deriveDevelopmentProject(next.type, next.subtypes) } : next;
    }),
  }));
  const toggleProjectSubtype = (index: number, value: string, on: boolean) => {
    const current = formData.developmentProjects[index]?.subtypes || [];
    updateProject(index, { subtypes: on ? [...current, value] : current.filter(s => s !== value) });
  };
  // Answering "yes" with no project yet opens one row to fill.
  useEffect(() => {
    if (formData.isRelatedToDevelopmentProject !== true) return;
    setFormData(prev => (prev.developmentProjects.length === 0 ? { ...prev, developmentProjects: [emptyDevelopmentProject()] } : prev));
  }, [formData.isRelatedToDevelopmentProject, setFormData]);

  const addPerpetrator = () => {
    setFormData(prev => ({
      ...prev,
      perpetrators: [...prev.perpetrators, { isOrganization: true, name: '', type: '' }]
    }));
  };

  const removePerpetrator = (index: number) => {
    setFormData(prev => ({
      ...prev,
      perpetrators: prev.perpetrators.filter((_, i) => i !== index)
    }));
  };

  const updatePerpetrator = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      perpetrators: prev.perpetrators.map((perp, i) =>
        i === index ? { ...perp, [field]: value } : perp
      )
    }));
  };

  const handleFilesSelected = (files: CaseFile[]) => {
    setFormData(prev => ({ ...prev, files }));
  };

  const handleLocationChange = (location: {
    latitude: number;
    longitude: number;
    address: string;
    country?: string;
    region?: string;
    city?: string;
  }) => {
    // Coordinates always follow the pin. Text fields fill in only where empty; if the map
    // disagrees with something already typed, ask before replacing it.
    const incoming: Array<[keyof CaseFormData, string | undefined, string]> = [
      ['country', location.country, 'Country'],
      ['region', location.region, 'Region'],
      ['city', location.city, 'City'],
      ['specificLocation', location.address, 'Specific location'],
    ];
    const conflicts = incoming.filter(([k, v]) => v && String(formData[k] || '').trim() && String(formData[k]).trim() !== v.trim());
    const apply = (replace: boolean) => setFormData(prev => {
      const next: any = { ...prev, latitude: location.latitude.toString(), longitude: location.longitude.toString() };
      for (const [k, v] of incoming) {
        if (!v) continue;
        if (!String(prev[k] || '').trim() || replace) next[k] = v;
      }
      return next;
    });
    if (conflicts.length === 0) { apply(true); return; }
    apply(false);
    void confirm({
      title: 'Update location details from the map?',
      message: `The map says: ${conflicts.map(([k, v, label]) => `${label}: ${formData[k]} → ${v}`).join('; ')}. Replace what you typed?`,
      confirmLabel: 'Use the map',
      cancelLabel: 'Keep mine',
    }).then((ok) => { if (ok) apply(true); });
  };

  const validateDocuments = (files: CaseFile[] = formData.files): string[] => {
    const errors: string[] = [];
    files.forEach((file, index) => {
      const num = index + 1;
      if (!file.category) errors.push(`Document ${num}: Category is required.`);
      if (!file.documentDate) errors.push(`Document ${num}: Date of document is required.`);
      if (!file.description.trim()) errors.push(`Document ${num}: Description is required.`);
    });
    return errors;
  };

  // Returns the blocking messages (empty when the form is clean) and highlights the offending
  // fields. The caller decides what to do with them — toast, or offer the admin bypass.
  // Pure: every rule, over the form as given. Keys are field paths (victims[2].gender, doc_0…).
  const computeErrors = (data: CaseFormData): Record<string, string> => {
    const allErrors: Record<string, string> = {};
    // Victims are numbered as the cards show them: individuals count among individuals only.
    const ordinal = (i: number) => data.victims.slice(0, i + 1).filter(v => !v.isGroup).length;

    // Step 1: Documenter Details (optional on AI-sourced cases — mirrors the backend rule)
    if (!isAutoSourced) {
      if (!data.documentorName.trim()) allErrors.documentorName = 'Documenter name is required.';
      if (!data.documentorOrganization.trim()) allErrors.documentorOrganization = 'Organization is required.';
    }
    if (data.documentorEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.documentorEmail))
      allErrors.documentorEmail = 'Please enter a valid email address.';

    // Step 2: Data Sharing Agreement
    if (!data.dataUsageRestriction) allErrors.dataUsageRestriction = 'Data sharing agreement is required.';

    // Step 3: Incident Details
    if (!data.description.trim()) allErrors.description = 'Description is required.';
    if (!data.country.trim()) allErrors.country = 'Country is required.';
    if (!data.caseStartDate) allErrors.caseStartDate = 'Case start date is required.';
    if (!data.latitude.trim() || !data.longitude.trim()) {
      allErrors.latitude = 'Location coordinates (latitude and longitude) are required.';
    } else {
      const lat = Number(data.latitude), lng = Number(data.longitude);
      if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
        allErrors.latitude = 'Coordinates must be numbers: latitude between -90 and 90, longitude between -180 and 180.';
      }
    }
    if (!data.isCaseOngoing && !data.caseEndDate) allErrors.caseEndDate = 'Case end date is required when the case has concluded.';

    // Step 4: Victim Details
    if (data.victims.length === 0) allErrors.victims = 'At least one victim is required.';
    else if (!data.victims.some(v => v.isIndigenous !== false)) allErrors.victims = 'At least one victim must be Indigenous.';
    // Validate Indigenous individual victims have indigenousGroupOfVictim
    data.victims.forEach((victim, index) => {
      if (!victim.isGroup && victim.isIndigenous !== false && !victim.indigenousGroupOfVictim?.trim()) {
        allErrors[`victims[${index}].indigenousGroupOfVictim`] = `Ethnic identity is required for individual victim ${ordinal(index)}.`;
      }
      if (victim.isGroup && victim.isIndigenous === false && !victim.groupType && !(victim.groupName || []).length) {
        allErrors[`victims[${index}].groupType`] = 'Choose what kind of non-Indigenous group was targeted, or give its name.';
      }
    });
    // Validate group victims have at least one count field
    data.victims.forEach((victim, index) => {
      if (victim.isGroup) {
        const hasCount = (victim.affectedIndividualsCount ?? 0) > 0 ||
          (victim.affectedFamiliesCount ?? 0) > 0 ||
          (victim.affectedHomesCount ?? 0) > 0 ||
          (victim.affectedGroupsCount ?? 0) > 0 ||
          (victim.affectedCommunitiesCount ?? 0) > 0 ||
          (victim.affectedVillagesCount ?? 0) > 0;
        if (!hasCount) {
          allErrors[`victims[${index}].groupCount`] = 'Group victims must have at least one group count filled (e.g. affected individuals, families, homes, groups, communities, or villages).';
        }
        // Target-scope derivation (whole community vs. multiple communities)
        // needs the community count when the group targeted is a community.
        if ((victim as any).groupType === 'COMMUNITY' && !((victim.affectedCommunitiesCount ?? 0) > 0)) {
          allErrors[`victims[${index}].affectedCommunitiesCount`] = 'When the targeted group is a community, fill in the number of affected communities.';
        }
        // A violation's gender or age breakdown may not exceed that violation's total affected.
        const demo: any = (victim as any).violationDemographics || {};
        const vKeys = Array.isArray(victim.violationTags) ? victim.violationTags : [];
        const AGE_KEYS = ['affected0to12Count', 'affected13to25Count', 'affected26to35Count', 'affected36to59Count', 'affected60PlusCount'];
        for (const k of vKeys) {
          const d = demo[k] || {};
          if (d.affectedCount == null) continue;
          const total = Number(d.affectedCount);
          const vlabel = enumLabel(k);
          const genderSum = (Number(d.affectedMaleCount) || 0) + (Number(d.affectedFemaleCount) || 0);
          const ageSum = AGE_KEYS.reduce((s, f) => s + (Number(d[f]) || 0), 0);
          if (genderSum > total) {
            allErrors[`victims[${index}].demo.${k}.gender`] = `Group of victims — ${vlabel}: gender counts add up to ${genderSum}, more than the ${total} affected. Fix or clear them.`;
          }
          if (ageSum > total) {
            allErrors[`victims[${index}].demo.${k}.age`] = `Group of victims — ${vlabel}: age counts add up to ${ageSum}, more than the ${total} affected. Fix or clear them.`;
          }
        }
      }
    });

    // Perpetrators: a row without a name is either unfinished or should be removed.
    data.perpetrators.forEach((p, i) => {
      if (!String(p.name || '').trim()) allErrors[`perpetrators[${i}].name`] = `Perpetrator ${i + 1}: give a name, or remove the row.`;
    });

    // Step 5: Categories of HR Violations
    if (data.violationTypes.length === 0) allErrors.violationTypes = 'At least one violation type is required.';
    if (data.violationTypes.includes('OTHER') && data.otherViolationTypes.length === 0)
      allErrors.otherViolationTypes = 'At least one other violation type must be specified when violation type is "Other".';
    if (data.otherViolationTypes.includes('OTHER') && !data.otherViolationTypeDetails.trim())
      allErrors.otherViolationTypeDetails = 'Please describe the other violation type.';
    // NOTE: FPIC_VIOLATION has no companion requirement. Step 5 shows a soft reminder to check the
    // related violations, but it must never block submission — do not add a rule for it here.


    // Document validation (applies to all submissions)
    const docErrors = validateDocuments(data.files);
    docErrors.forEach((err, i) => { allErrors[`doc_${i}`] = err; });

    return allErrors;
  };

  // Full validation on Submit. From then on the errors re-compute as the form changes, so a
  // highlight clears the moment its field is fixed and never goes stale.
  const [validatedOnce, setValidatedOnce] = useState(false);
  const runValidation = (): Record<string, string> => {
    const allErrors = computeErrors(formData);
    setStepErrors(allErrors);
    setValidatedOnce(true);
    return allErrors;
  };
  const runDocValidation = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    validateDocuments(formData.files).forEach((e, i) => { errs[`doc_${i}`] = e; });
    setStepErrors(errs);
    return errs;
  };
  useEffect(() => {
    if (validatedOnce) setStepErrors(computeErrors(formData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, validatedOnce]);

  // Which screen a problem lives on, so Submit can take the user there.
  const stepForKey = (k: string): number =>
    k.startsWith('documentor') || k === 'isPrimaryData' || k === 'dataBasis' || k === 'sourceInformation' ? 1
    : k === 'dataUsageRestriction' || k === 'willingToShareMoreInfo' ? 2
    : ['description', 'country', 'caseStartDate', 'caseEndDate', 'latitude', 'longitude', 'region', 'city', 'specificLocation', 'indigenousTerritory'].includes(k) ? 3
    : k.startsWith('violationTypes') || k.startsWith('otherViolationType') ? 4
    : k.startsWith('perpetrators') || k.startsWith('developmentProject') || k === 'isRelatedToDevelopmentProject' ? 5
    : k.startsWith('victims') || k === 'indigenousGroup' ? 6
    : ['impactTypes', 'familyImpact', 'communityImpact'].includes(k) ? 7
    : k.startsWith('investigation') || k.startsWith('advocacy') ? 8
    : k.startsWith('doc_') ? 9
    : currentStep;
  const errorsByStep = (errs: Record<string, string> = stepErrors) => {
    const out: Record<number, Array<[string, string]>> = {};
    for (const [k, m] of Object.entries(errs)) (out[stepForKey(k)] ||= []).push([k, m]);
    return out;
  };
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  const goToError = (key: string) => {
    setCurrentStep(stepForKey(key));
    setPendingFocus(key);
  };
  useEffect(() => {
    if (!pendingFocus) return;
    const el = document.getElementById(`err-${pendingFocus}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const prev = el.previousElementSibling as HTMLElement | null;
      const focusable = prev && (/^(INPUT|SELECT|TEXTAREA)$/.test(prev.tagName) || prev.isContentEditable) ? prev : prev?.querySelector?.('input, select, textarea, [contenteditable="true"]') as HTMLElement | null;
      focusable?.focus?.({ preventScroll: true });
    }
    setPendingFocus(null);
  }, [pendingFocus, currentStep]);

  // One way in for both save buttons. A clean form just saves. A form with problems is refused,
  // unless the user is allowed to bypass — then they get a dialog naming what failed and have to
  // say so explicitly; only then does the save go through, carrying the opt-in to the backend.
  const attemptSubmit = async (asDraft: boolean, errors: Record<string, string>) => {
    const blockingErrors = Object.values(errors);
    if (blockingErrors.length === 0) {
      onSubmit(asDraft);
      return;
    }
    if (!allowValidationBypass) {
      const keys = Object.keys(errors).sort((a, b) => stepForKey(a) - stepForKey(b));
      goToError(keys[0]);
      showToast('error', `${blockingErrors.length} problem${blockingErrors.length === 1 ? '' : 's'} to fix before this can be saved. The fields are highlighted.`);
      return;
    }
    const shown = blockingErrors.slice(0, 4).join(' ');
    const rest = blockingErrors.length > 4 ? ` And ${blockingErrors.length - 4} more.` : '';
    const confirmed = await confirm({
      title: 'Save without passing validation?',
      message: `${blockingErrors.length} check${blockingErrors.length === 1 ? '' : 's'} did not pass. ${shown}${rest} As an admin you can save anyway — the case will be stored incomplete, and the bypass is recorded in the audit log.`,
      confirmLabel: 'Save anyway',
      cancelLabel: 'Go back and fix',
      variant: 'danger',
    });
    if (confirmed) onSubmit(asDraft, true);
  };

  const handleNext = () => {
    if (currentStep >= TOTAL_STEPS) return;
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < TOTAL_STEPS) return;
    void attemptSubmit(false, runValidation());
  };

  // Persistent summary of what blocks saving: this step's problems as links to the field, and a
  // pointer to the other steps that have some. Replaces the old five-second toast list.
  const renderErrorSummary = () => {
    const byStep = errorsByStep();
    const total = Object.keys(stepErrors).length;
    if (total === 0) return null;
    const here = byStep[currentStep] || [];
    const elsewhere = Object.keys(byStep).map(Number).filter(n => n !== currentStep && byStep[n].length).sort((a, b) => a - b);
    return (
      <div className="mb-6 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-900 dark:text-red-200" role="alert">
        <p className="font-semibold">
          {here.length > 0
            ? `${here.length} problem${here.length === 1 ? '' : 's'} on this step`
            : `Nothing to fix on this step, ${total} elsewhere`}
        </p>
        {here.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {here.map(([k, m]) => (
              <li key={k}>
                <button type="button" onClick={() => goToError(k)} className="text-left underline decoration-dotted underline-offset-2 hover:decoration-solid">{m}</button>
              </li>
            ))}
          </ul>
        )}
        {elsewhere.length > 0 && (
          <p className="mt-2 text-xs">
            {here.length > 0 ? 'Also on ' : 'On '}
            {elsewhere.map((n, i) => (
              <React.Fragment key={n}>
                {i > 0 && ', '}
                <button type="button" onClick={() => goToError(byStep[n][0][0])} className="underline decoration-dotted underline-offset-2 hover:decoration-solid">
                  Step {n} · {STEP_LABELS[n - 1]} ({byStep[n].length})
                </button>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    );
  };

  // Clickable stepper. Every step shows where it stands: current, N to fix (once visited, or once
  // Submit has run), done, or not yet visited. Steps are freely navigable.
  const renderStepper = () => {
    const live = validatedOnce ? stepErrors : computeErrors(formData);
    const byStep = errorsByStep(live);
    return (
      <nav aria-label="Form steps" className="mb-6">
        <ol className="flex flex-wrap gap-1.5">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const known = visited.has(n) || validatedOnce;
            const count = known ? (byStep[n] || []).length : 0;
            const isCurrent = n === currentStep;
            const state = isCurrent ? 'current' : count > 0 ? 'error' : known ? 'done' : 'todo';
            const ring = state === 'current' ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
              : state === 'error' ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200'
              : state === 'done' ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500';
            const dot = state === 'error' ? 'bg-red-600 text-white' : state === 'done' ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200' : state === 'current' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500';
            return (
              <li key={n}>
                <button
                  type="button"
                  onClick={() => { setCurrentStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  aria-current={isCurrent ? 'step' : undefined}
                  title={count > 0 ? `${label}: ${count} to fix` : label}
                  className={`inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 text-xs font-medium transition-colors hover:border-emerald-400 ${ring}`}
                >
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${dot}`}>
                    {state === 'error' ? count : state === 'done' ? '✓' : n}
                  </span>
                  <span className={isCurrent ? '' : 'hidden sm:inline'}>{label}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Step {currentStep} of {TOTAL_STEPS} · {STEP_LABELS[currentStep - 1]}</p>
      </nav>
    );
  };

  // AI provenance mark next to a field's label (Edit only). Undo puts the prior value back into
  // the form state too, so what the user sees matches what the server now holds.
  const applyAiUndo = (path: string, prior: unknown) => {
    const norm = (cur: unknown, v: unknown): any => {
      if (v === null || v === undefined) return Array.isArray(cur) ? [] : typeof cur === 'boolean' ? false : '';
      if (typeof cur === 'string' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
      return v;
    };
    let m = /^victims\[(.+?)\]\.violation\[(.+?)\]\.(\w+)$/.exec(path);
    if (m) {
      const [, vid, key, f] = m;
      setFormData(prev => ({ ...prev, victims: prev.victims.map(v => v.id === vid ? { ...v, violationDemographics: { ...(v.violationDemographics || {}), [key]: { ...((v.violationDemographics || {})[key] || {}), [f]: prior as any } } } : v) }));
      return;
    }
    m = /^victims\[(.+?)\]\.(\w+)$/.exec(path);
    if (m) {
      const [, vid, f] = m;
      setFormData(prev => ({ ...prev, victims: prev.victims.map(v => v.id === vid ? { ...v, [f]: norm((v as any)[f], prior) } : v) }));
      return;
    }
    m = /^violationDetails\[(.+?)\]\.(\w+)$/.exec(path);
    if (m) {
      const [, did, f] = m;
      setFormData(prev => ({ ...prev, violationDetails: prev.violationDetails.map(d => (d as any).id === did ? { ...d, [f]: norm((d as any)[f], prior) } : d) }));
      return;
    }
    m = /^developmentProjects\[(.+?)\]\.(\w+)$/.exec(path);
    if (m) {
      const [, pid, f] = m;
      // startYear is text on the form; the ledger holds the integer.
      const val = f === 'startYear' ? (prior == null ? '' : String(prior)) : prior;
      setFormData(prev => ({ ...prev, developmentProjects: prev.developmentProjects.map((p, i) => (p.id === pid || String(i) === pid) ? { ...p, [f]: norm((p as any)[f], val) } : p) }));
      return;
    }
    if (/^\w+$/.test(path) && path in formData) {
      setFormData(prev => ({ ...prev, [path]: norm((prev as any)[path], prior) }));
    }
  };
  const aiMark = (path: string | string[]) =>
    aiMeta && caseId ? <AiFieldMark field={path} caseId={caseId} ai={aiMeta} onChange={onAiChange} onUndone={applyAiUndo} /> : null;

  // Yes / No / Not asked. In this data "not recorded" and "no" are different facts, so the
  // question starts unanswered and only a click sets it.
  // Two-way choices that shape the record (how the case was learned about, what IPRI may do with
  // it) are presented as cards: a plain-language title, what it means, and when to choose it.
  // One choice sets the basis and keeps the old boolean mirror in step for the API.
  const setDataBasis = (b: DataBasis) => { updateFormData('dataBasis', b); updateFormData('isPrimaryData', b !== 'SECONDARY'); };

  const choiceCard = (o: { name: string; checked: boolean; onSelect: () => void; title: string; body: string; when: string; footnote?: string }) => (
    <label className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${o.checked
      ? 'border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/10'
      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`}>
      <input type="radio" name={o.name} checked={o.checked} onChange={o.onSelect}
        className="mt-1 rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{o.title}</span>
        <span className="block mt-1 text-sm text-gray-700 dark:text-gray-300">{o.body}</span>
        <span className="block mt-2 text-xs text-gray-600 dark:text-gray-400"><span className="font-medium">Choose this when:</span> {o.when}</span>
        {o.footnote && <span className="block mt-2 text-[11px] text-gray-500 dark:text-gray-500">{o.footnote}</span>}
      </span>
    </label>
  );

  const triState = (field: 'advocacyUndertaken' | 'investigationConducted' | 'isRelatedToDevelopmentProject', question: string) => {
    const v = formData[field];
    const opt = (label: string, val: boolean | null) => (
      <label className="inline-flex items-center gap-1.5 mr-5 text-sm text-gray-700 dark:text-gray-300">
        <input type="radio" name={`tri-${field}`} checked={v === val} onChange={() => updateFormData(field, val)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
        {label}
      </label>
    );
    return (
      <fieldset className="mb-3">
        <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{question}{aiMark(field)}</legend>
        <div>{opt('Yes', true)}{opt('No', false)}{opt('Not asked / unknown', null)}</div>
      </fieldset>
    );
  };

  // Definitions from the codebook (types/taxonomy.ts). Hovering or focusing a pill shows its
  // definition card; "Definitions" beside a list's label opens the glossary drawer for that list.
  // Selects show the definition of the chosen value beneath the control.
  const [glossary, setGlossary] = useState<string | null>(null);
  const defsLink = (listKey: string) => (
    <button
      type="button"
      onClick={() => setGlossary(prev => (prev === listKey ? null : listKey))}
      aria-expanded={glossary === listKey}
      className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline align-baseline"
    >
      <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
      Definitions
    </button>
  );
  const selectedDef = (listKey: string, value: string | null | undefined) => {
    const en = defineValue(listKey, value);
    return en ? <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{en.definition}</p> : null;
  };
  const listRule = (listKey: string) => taxonomyByKey(listKey)?.rule;
  // Every coded list is shown alphabetically by label, with the catch-all codes last.
  const LAST_CODES = new Set(['OTHER', 'NO_INFORMATION', 'ND']);
  const sortOptions = <T extends { value: string; label: string }>(opts: T[]): T[] =>
    [...opts].sort((a, b) => (LAST_CODES.has(a.value) ? 1 : 0) - (LAST_CODES.has(b.value) ? 1 : 0) || a.label.localeCompare(b.label));

  // Multi-select as pills, the same presentation as the violation types: neutral grey (violation
  // colours stay the only colour code), a check mark and aria-pressed when selected, the
  // definition card on hover or focus.
  const PILL_ON = 'bg-gray-800 text-white border-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100';
  const renderCodePills = (
    listKey: string,
    options: { value: string; label: string }[],
    selected: string[],
    onToggle: (value: string, next: boolean) => void,
    groups?: { label: string; types: { value: string; label: string }[] }[],
  ) => {
    const pill = (opt: { value: string; label: string }) => {
      const on = selected.includes(opt.value);
      return (
        <DefinitionHover key={opt.value} listKey={listKey} value={opt.value}>
          <button
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(opt.value, !on)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? PILL_ON : NEUTRAL_BADGE}`}
          >
            {on ? '✓ ' : ''}{opt.label}
          </button>
        </DefinitionHover>
      );
    };
    if (!groups) return <div className="flex flex-wrap gap-2">{sortOptions(options).map(pill)}</div>;
    const taxGroups = taxonomyByKey(listKey)?.groups || [];
    return (
      <div className="space-y-4">
        {groups.map(group => (
          <div key={group.label}>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">{group.label}</h4>
            {taxGroups.find(g => g.label.toLowerCase() === group.label.toLowerCase())?.note && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{taxGroups.find(g => g.label.toLowerCase() === group.label.toLowerCase())?.note}</p>
            )}
            <div className="flex flex-wrap gap-2">{sortOptions(group.types).map(pill)}</div>
          </div>
        ))}
      </div>
    );
  };

  // Pairings the codebook expects (arbitrary detention with criminalization, land grabbing with
  // FPIC…): a soft reminder with a one-click add. Never blocks; "Not in this case" hides it.
  const [dismissedPairings, setDismissedPairings] = useState<Set<string>>(new Set());
  const isViolationSelected = (code: string) => formData.violationTypes.includes(code) || formData.otherViolationTypes.includes(code);
  const addViolation = (code: string) => {
    if (code === 'KILLING' || code === 'CRIMINALIZATION') void handleViolationTypeChange(code, true);
    else void handleOtherViolationToggle(code);
  };
  const violationLabel = (code: string) => defineValue('violationTypes', code)?.label || getViolationTypeLabelSafe(code);
  const fpicQuestionShowing = formData.otherViolationTypes.some(t => FPIC_RELATED_TYPES.includes(t)) && !formData.otherViolationTypes.includes('FPIC_VIOLATION');
  const activePairings = VIOLATION_PAIRINGS.filter(pr =>
    pr.when.every(isViolationSelected)
    && !isViolationSelected(pr.suggest)
    && !dismissedPairings.has(pr.when.join('+') + '>' + pr.suggest)
    && !(pr.suggest === 'FPIC_VIOLATION' && fpicQuestionShowing)
  );

  // Helper for field error display
  const fieldError = (field: string) =>
    stepErrors[field] ? (
      <p id={`err-${field}`} role="alert" className="mt-1 text-sm text-red-600">{stepErrors[field]}</p>
    ) : null;

  const errorClass = (field: string) =>
    stepErrors[field] ? 'border-red-300' : 'border-gray-300 dark:border-gray-600';
  // Ties a control to its inline error for assistive tech.
  const errorProps = (field: string) =>
    stepErrors[field] ? { 'aria-invalid': true as const, 'aria-describedby': `err-${field}` } : {};

  // ============ STEP 1: Documenter Details ============
  const renderStep1 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Documenter Details</h2>
      <div className="space-y-6">
        {isAutoSourced && (
          <p className="text-sm text-gray-600 dark:text-gray-400 rounded-md bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2">
            This case was gathered by the automated pipeline, so there is no human documenter. Name and organization are optional here; add them only if someone at IPRI or a partner has since documented the case.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="your-name-documenter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your Name (Documenter){!isAutoSourced && ' *'}{aiMark('documentorName')}</label>
            <input id="your-name-documenter" {...errorProps('documentorName')}
              type="text"
              value={formData.documentorName}
              onChange={(e) => updateFormData('documentorName', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorName')}`}
              placeholder="Your full name"
            />
            {fieldError('documentorName')}
          </div>
          <div>
            <label htmlFor="organization-affiliation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization/Affiliation{!isAutoSourced && ' *'}{aiMark('documentorOrganization')}</label>
            <input id="organization-affiliation" {...errorProps('documentorOrganization')}
              type="text"
              value={formData.documentorOrganization}
              onChange={(e) => updateFormData('documentorOrganization', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorOrganization')}`}
              placeholder="Organization name"
            />
            {fieldError('documentorOrganization')}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address{aiMark('documentorEmail')}</label>
            <input id="email-address" {...errorProps('documentorEmail')}
              type="email"
              value={formData.documentorEmail}
              onChange={(e) => updateFormData('documentorEmail', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorEmail')}`}
              placeholder="your.email@example.com"
            />
            {fieldError('documentorEmail')}
          </div>
          <div>
            <label htmlFor="phone-number-whatsapp-signal" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number (WhatsApp/Signal){aiMark('documentorPhone')}</label>
            <input id="phone-number-whatsapp-signal"
              type="tel"
              value={formData.documentorPhone}
              onChange={(e) => updateFormData('documentorPhone', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="+1234567890"
            />
          </div>
        </div>
        <div>
          <span id="lbl-data-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">How do you know about this case?</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">This sets how much the record can be trusted on its own.</p>
          <div role="radiogroup" aria-labelledby="lbl-data-type" className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {choiceCard({
              name: 'data-type',
              checked: formData.dataBasis === 'PRIMARY',
              onSelect: () => setDataBasis('PRIMARY'),
              title: 'First-hand: we documented it ourselves',
              body: 'IPRI or a partner heard it directly from the victims, witnesses or community, visited the place, or received their own written account.',
              when: 'interviews, testimonies, field visits, community reports, a partner organisation\u2019s own documentation.',
              footnote: 'Recorded as primary data.',
            })}
            {choiceCard({
              name: 'data-type',
              checked: formData.dataBasis === 'SECONDARY',
              onSelect: () => setDataBasis('SECONDARY'),
              title: 'Second-hand: from something already published',
              body: 'The case comes from a news article, an NGO or UN report, a court record or a social media post that someone else produced.',
              when: 'you are transcribing or summarising a source rather than documenting the events yourself.',
              footnote: 'Recorded as secondary data.',
            })}
            {choiceCard({
              name: 'data-type',
              checked: formData.dataBasis === 'MIXED',
              onSelect: () => setDataBasis('MIXED'),
              title: 'Both: we documented it ourselves and also used published reports',
              body: 'Part of the case comes from IPRI or a partner\u2019s own documentation and part from news, NGO or UN reporting, court records or posts.',
              when: 'you have first-hand material and you also drew on published sources for some of the facts.',
              footnote: 'Recorded as mixed data. Treated like primary data: never sent to the AI.',
            })}
          </div>
        </div>

        <div>
          <label htmlFor="source-information-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source Information/Notes{aiMark('sourceInformation')}</label>
          <RichTextEditor id="source-information-notes"
            rows={3}
            value={formData.sourceInformation}
            onChange={(v) => updateFormData('sourceInformation', v)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
            placeholder={formData.dataBasis === 'SECONDARY'
              ? 'The article, report or post this case comes from: paste the link and any notes about the source…'
              : formData.dataBasis === 'MIXED'
                ? 'How this case was documented, and the articles or reports also used: paste the links and any notes…'
                : 'How this case was documented: interviews, field visit, community report, partner organisation…'}
          />
        </div>

        <div>
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.isLDSFApplicant}
              onChange={(e) => {
                const checked = e.target.checked;
                updateFormData('isLDSFApplicant', checked);
                if (!checked) {
                  updateFormData('ldsfPaymentCompletedDate', '');
                }
              }}
              className="mt-1 rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
            />
            <span className="ml-3">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Is this case an LDSF Applicant?</span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">Indicate whether this case is from a Legal Defense and Sanctuary Fund applicant</span>
            </span>
          </label>
        </div>

        {formData.isLDSFApplicant && (
          <div>
            <label htmlFor="ldsf-payment-completed-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">LDSF Payment Completed Date{aiMark('ldsfPaymentCompletedDate')}</label>
            <input id="ldsf-payment-completed-date"
              type="date"
              value={formData.ldsfPaymentCompletedDate}
              onChange={(e) => updateFormData('ldsfPaymentCompletedDate', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Date when LDSF payment was completed; determines reporting year</p>
          </div>
        )}
      </div>
    </div>
  );

  // ============ STEP 2: Data Sharing Agreement ============
  const renderStep2 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Data Sharing Agreement</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">What is IPRI allowed to do with this case? This is normally agreed with the source or the community when the case is documented, and it decides who can see names and details later.</p>
      <div className="space-y-6">
        <div>
          <span id="lbl-data-usage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Permitted use *</span>
          {fieldError('dataUsageRestriction')}
          <div role="radiogroup" aria-labelledby="lbl-data-usage" className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {choiceCard({
              name: 'data-usage',
              checked: formData.dataUsageRestriction === 'IPRI_ONLY_TRENDS',
              onSelect: () => updateFormData('dataUsageRestriction', 'IPRI_ONLY_TRENDS'),
              title: 'Count it in trends only',
              body: 'The case adds to statistics and trend analysis. The names, the story and anything that could identify the people involved stay inside IPRI and are never published.',
              when: 'the source or community has not agreed to publication, or naming anyone could put them at risk. If in doubt, choose this.',
              footnote: 'Identifiers are hidden from Documenters and Researchers by default, and left out of the PDF unless an Approver exports an internal copy.',
            })}
            {choiceCard({
              name: 'data-usage',
              checked: formData.dataUsageRestriction === 'IPRI_WITH_DETAILS',
              onSelect: () => updateFormData('dataUsageRestriction', 'IPRI_WITH_DETAILS'),
              title: 'Publish the case with its details',
              body: 'The case may appear in IPRI\u2019s public database with its description, location and details, and be cited in reports.',
              when: 'consent for publication exists and the details are already public or add no risk to the victims.',
              footnote: 'Identifiers are shown to every reviewer and included in the PDF.',
            })}
          </div>
        </div>

        {/* Follow-up consent: belongs with the sharing terms, not at the end of the form */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.willingToShareMoreInfo}
              onChange={(e) => updateFormData('willingToShareMoreInfo', e.target.checked)}
              className="mt-1 rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
            />
            <span className="ml-3">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">IPRI may follow up with the documenter</span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">If reviewers have questions or need more detail, they may contact the documenter using the details on step 1. Leave unticked if the documenter has not agreed to this.</span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  // ============ STEP 3: Incident Details ============
  const renderStep3 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Incident Details</h2>
      <div className="space-y-6">
        {/* Date of Submission */}
        <div>
          <label htmlFor="date-of-submission" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date of Submission{aiMark('reportedDate')}</label>
          <input id="date-of-submission"
            type="date"
            value={formData.reportedDate}
            onChange={(e) => updateFormData('reportedDate', e.target.value)}
            className="mt-1 block w-full md:w-1/3 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description *{aiMark('description')}</label>
          <RichTextEditor id="description" {...errorProps('description')}
            rows={4}
            value={formData.description}
            onChange={(v) => updateFormData('description', v)}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('description')}`}
            style={{ resize: 'vertical' }}
            placeholder="Describe the incident in detail..."
          />
          {fieldError('description')}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {formData.description.length} characters (minimum 20 required)
          </p>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700"></div>

        {/* Date section */}
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isCaseOngoing}
              onChange={(e) => {
                updateFormData('isCaseOngoing', e.target.checked);
                if (e.target.checked) updateFormData('caseEndDate', '');
              }}
              className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">This is an ongoing case</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date of the Incident *</label>
              <div className="flex items-start gap-3">
                <select
                  value={formData.caseStartDatePrecision}
                  onChange={(e) => {
                    // Coarser precision keeps the year (and month); finer precision can't invent
                    // the missing part, so it starts empty.
                    const next = e.target.value as 'DAY' | 'MONTH' | 'YEAR';
                    const cur = formData.caseStartDate;
                    const kept = next === 'YEAR' ? cur.slice(0, 4)
                      : next === 'MONTH' ? (formData.caseStartDatePrecision === 'DAY' ? cur.slice(0, 7) : '')
                      : '';
                    updateFormData('caseStartDatePrecision', next);
                    updateFormData('caseStartDate', kept);
                  }}
                  className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                >
                  <option value="DAY">Full date</option>
                  <option value="MONTH">Year & month</option>
                  <option value="YEAR">Year only</option>
                </select>
                {formData.caseStartDatePrecision === 'DAY' && (
                  <input {...errorProps('caseStartDate')}
                    type="date"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
                {formData.caseStartDatePrecision === 'MONTH' && (
                  <input {...errorProps('caseStartDate')}
                    type="month"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
                {formData.caseStartDatePrecision === 'YEAR' && (
                  <input {...errorProps('caseStartDate')}
                    type="number"
                    min="1900"
                    max="2100"
                    placeholder="e.g., 2024"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
              </div>
              {fieldError('caseStartDate')}
              <div className="mt-3">
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time <span className="font-normal text-gray-500 dark:text-gray-400">(local time where the incident happened)</span>{aiMark('incidentTime')}</label>
                <input id="time"
                  type="time"
                  value={formData.incidentTime}
                  onChange={(e) => updateFormData('incidentTime', e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${formData.isCaseOngoing ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>End Date of the Incident{!formData.isCaseOngoing && ' *'}</label>
              <input
                type="date"
                value={formData.isCaseOngoing ? '' : formData.caseEndDate}
                onChange={(e) => updateFormData('caseEndDate', e.target.value)}
                disabled={formData.isCaseOngoing}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${formData.isCaseOngoing ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
              />
{fieldError('caseEndDate')}
            </div>
          </div>
        </div>

        {/* Location section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Location *</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Pin the place on the map or search for it. Coordinates and country are required; the other fields fill in from the map and can be edited.</p>
          <div className="space-y-6">
            <LocationPicker
              latitude={formData.latitude ? parseFloat(formData.latitude) : undefined}
              longitude={formData.longitude ? parseFloat(formData.longitude) : undefined}
              address={formData.specificLocation}
              onLocationChange={(loc) => {
                handleLocationChange(loc);
                setEditingLocation(false);
              }}
            />
            {fieldError('latitude')}

            {/* Location details - read-only by default */}
            {(formData.latitude || formData.country) && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Location Details</h4>
                  <button
                    type="button"
                    onClick={() => setEditingLocation(!editingLocation)}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    {editingLocation ? 'Done' : 'Edit'}
                  </button>
                </div>

                {editingLocation ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="country" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Country *{aiMark('country')}</label>
                        <ComboSelect id="country"
                          options={countries}
                          value={formData.country}
                          onChange={(val) => updateFormData('country', val)}
                          placeholder="Search country..."
                          allowUnknown={false}
                        />
                        {fieldError('country')}
                      </div>
                      <div>
                        <label htmlFor="region-state" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Region/State{aiMark('region')}</label>
                        <input id="region-state"
                          type="text"
                          value={formData.region}
                          onChange={(e) => updateFormData('region', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">City{aiMark('city')}</label>
                        <input id="city"
                          type="text"
                          value={formData.city}
                          onChange={(e) => updateFormData('city', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="specific-location" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Specific Location{aiMark('specificLocation')}</label>
                        <input id="specific-location"
                          type="text"
                          value={formData.specificLocation}
                          onChange={(e) => updateFormData('specificLocation', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        {/* Was in the data model and the case page but never in the form — hence 82% of cases without it. */}
                        <label htmlFor="indigenous-territory" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Indigenous Territory{aiMark('indigenousTerritory')}</label>
                        <input id="indigenous-territory"
                          type="text"
                          value={formData.indigenousTerritory}
                          onChange={(e) => updateFormData('indigenousTerritory', e.target.value)}
                          placeholder="Ancestral domain, reserve, resguardo, titled or customary territory"
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="latitude" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Latitude{aiMark('latitude')}</label>
                        <input id="latitude"
                          type="number"
                          step="any"
                          min={-90}
                          max={90}
                          value={formData.latitude}
                          onChange={(e) => updateFormData('latitude', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="longitude" className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Longitude{aiMark('longitude')}</label>
                        <input id="longitude"
                          type="number"
                          step="any"
                          min={-180}
                          max={180}
                          value={formData.longitude}
                          onChange={(e) => updateFormData('longitude', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Country</span>
                      <p className="text-gray-900 font-medium">{formData.country || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Region/State</span>
                      <p className="text-gray-900">{formData.region || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">City</span>
                      <p className="text-gray-900">{formData.city || '—'}</p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Specific Location</span>
                      <p className="text-gray-900">{formData.specificLocation || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Indigenous Territory</span>
                      <p className="text-gray-900">{formData.indigenousTerritory || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Coordinates</span>
                      <p className="text-gray-900 font-mono text-xs">{formData.latitude && formData.longitude ? `${formData.latitude}, ${formData.longitude}` : '—'}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );

  // ============ Victim Details (rendered as step 6) ============
  const renderStep4 = () => (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Who were the victims?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Record people one by one as individuals, and/or together as a group. Add at least one. Non-Indigenous people targeted in the same incident (lawyers, protesters, supporters) can be recorded too, alongside at least one Indigenous victim.</p>
        {fieldError('victims')}
      </div>

      <div className="border-b border-gray-300 dark:border-gray-600 pb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">1. Individual victims</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">People recorded one by one — named, or anonymous but individually described (for example &quot;a 16-year-old girl&quot;).</p>
      </div>

      {/* Individual Victims */}
      {(
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Individual victims</h2>
          </div>
          <button type="button" onClick={() => addVictim()} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
            Add Individual
          </button>
        </div>

        {formData.victims.filter(v => !v.isGroup).length === 0 ? (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <p className="text-sm">No individual victims added yet.</p>
            <p className="text-xs mt-1">Click "Add Individual" to add a victim.</p>
          </div>
        ) : (
        <div className="space-y-6">
          {formData.victims.map((victim, index) => (
            !victim.isGroup && (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">Individual victim {formData.victims.slice(0, index + 1).filter(v => !v.isGroup).length}</span>
                  <label htmlFor={`non-indigenous-${index}`} className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                    <input id={`non-indigenous-${index}`} type="checkbox" checked={victim.isIndigenous === false}
                      onChange={(e) => setFormData(prev => ({ ...prev, victims: prev.victims.map((v, i) => i !== index ? v
                        : (e.target.checked ? { ...v, isIndigenous: false, indigenousGroupOfVictim: '' } : { ...v, isIndigenous: true })) }))}
                      className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500" />
                    This person is not Indigenous{aiMark(`victims[${victim.id}].isIndigenous`)}
                  </label>
                </div>
                <button type="button" onClick={() => removeVictim(index)} className="text-red-600 text-sm hover:text-red-800">Remove</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`first-name-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name{aiMark(`victims[${victim.id}].firstName`)}</label>
                  <input id={`first-name-${index}`} type="text" value={victim.firstName || ''} onChange={(e) => updateVictim(index, 'firstName', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                <div>
                  <label htmlFor={`last-name-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name{aiMark(`victims[${victim.id}].lastName`)}</label>
                  <input id={`last-name-${index}`} type="text" value={victim.lastName || ''} onChange={(e) => updateVictim(index, 'lastName', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`date-of-birth-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date of Birth{aiMark(`victims[${victim.id}].dateOfBirth`)}</label>
                  <input id={`date-of-birth-${index}`} type="date" value={victim.dateOfBirth || ''} onChange={(e) => updateVictim(index, 'dateOfBirth', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                <div>
                  <label htmlFor={`age-bracket-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Age Bracket{aiMark(`victims[${victim.id}].ageBracket`)}</label>
                  <select id={`age-bracket-${index}`} value={victim.ageBracket || ''} onChange={(e) => updateVictim(index, 'ageBracket', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    {sortOptions(AGE_BRACKETS).map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`gender-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gender{aiMark(`victims[${victim.id}].gender`)}</label>
                  <select id={`gender-${index}`} value={victim.gender || ''} onChange={(e) => updateVictim(index, 'gender', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
                <div>
                  <label htmlFor={`dependent-type-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dependent Type{aiMark(`victims[${victim.id}].dependentType`)}</label>
                  <select id={`dependent-type-${index}`} value={victim.dependentType || ''} onChange={(e) => updateVictim(index, 'dependentType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    {sortOptions(DEPENDENT_TYPES).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`community-role-local-title-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Community Role (local title){aiMark(`victims[${victim.id}].communityRole`)}</label>
                  <input id={`community-role-local-title-${index}`} type="text" value={victim.communityRole || ''} onChange={(e) => updateVictim(index, 'communityRole', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., datu, timuay, cacique, lonko..." />
                </div>
                <div>
                  <label htmlFor={`community-role-category-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Community Role Category{aiMark(`victims[${victim.id}].communityRoleType`)}</label>
                  <select id={`community-role-category-${index}`} value={victim.communityRoleType || ''} onChange={(e) => updateVictim(index, 'communityRoleType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">No information</option>
                    {sortOptions(COMMUNITY_ROLE_TYPES).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                  </select>
                  {selectedDef('communityRoleType', victim.communityRoleType || '')}
                </div>
              </div>
              {victim.isIndigenous !== false && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`indigenous-group-of-victim-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Indigenous Group of Victim *{aiMark('indigenousGroup')}</label>
                  <ComboSelect id={`indigenous-group-of-victim-${index}`}
                    options={formData.indigenousGroup}
                    value={victim.indigenousGroupOfVictim || ''}
                    onChange={(val) => updateVictim(index, 'indigenousGroupOfVictim', val)}
                    onAddNew={(val) => updateFormData('indigenousGroup', [...formData.indigenousGroup, val])}
                    placeholder="Select or type group..."
                  />
                  {fieldError(`victims[${index}].indigenousGroupOfVictim`)}
                </div>
              </div>
              )}
              {victim.dependentType === 'WD' && (
                <div>
                  <label htmlFor={`dependent-details-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dependent Details{aiMark(`victims[${victim.id}].dependentDetails`)}</label>
                  <RichTextEditor id={`dependent-details-${index}`} value={victim.dependentDetails || ''} onChange={(v) => updateVictim(index, 'dependentDetails', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Details about dependents..." />
                </div>
              )}
              <div>
                <label htmlFor={`additional-info-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional details{aiMark(`victims[${victim.id}].additionalInfo`)}</label>
                <RichTextEditor id={`additional-info-${index}`} value={victim.additionalInfo || ''} onChange={(v) => updateVictim(index, 'additionalInfo', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Anything else about this person the record should hold: background, circumstances, what happened to them since…" />
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                {renderVictimViolationPills(index)}
              </div>
            </div>
            )
          ))}
        </div>
        )}
      </div>
      )}

      <div className="border-b border-gray-300 dark:border-gray-600 pb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">2. Groups of victims</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">People recorded together as a headcount, not one by one. Count only people who are not already listed as individuals above.</p>
      </div>

      {/* Groups of victims — the Indigenous group, each non-Indigenous group, then a slot to add another */}
      {[
        { indigenous: true, gIdx: formData.victims.findIndex(v => isGroupOf(v, true)) },
        ...formData.victims.map((v, i) => ({ indigenous: false, gIdx: isGroupOf(v, false) ? i : -1 })).filter(slot => slot.gIdx !== -1),
        { indigenous: false, gIdx: -1 },
      ].map(({ indigenous, gIdx }) => {
        // Once a non-Indigenous group exists, the slot for another is just a button, not an empty card.
        if (!indigenous && gIdx === -1 && formData.victims.some(v => isGroupOf(v, false))) {
          return (
            <div key="other-group-add" className="flex justify-end">
              <button type="button" onClick={() => addGroupVictim(false)} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">Add another non-Indigenous group</button>
            </div>
          );
        }
        const groupVictim = gIdx === -1 ? undefined : formData.victims[gIdx];
        const fid = indigenous ? 'group' : `other-group-${gIdx}`;
        const updateGroupField = (field: string, value: any) => {
          setFormData(prev => {
            const idx = gIdx;
            if (idx === -1) {
              return { ...prev, victims: [...prev.victims, { isGroup: true, ...(indigenous ? {} : { isIndigenous: false }), [field]: value, violationTags: violationKeysOf(prev) }] };
            }
            return { ...prev, victims: prev.victims.map((v, i) => {
              if (i !== idx) return v;
              const updated: any = { ...v, [field]: value };
              // When the group's total affected-individuals changes, roll it into any selected
              // violation whose count is still empty or still equal to the previous total
              // (i.e. not manually customized). Counts the user has changed are left alone.
              if (field === 'affectedIndividualsCount') {
                const oldVal = (v as any).affectedIndividualsCount;
                const tags = Array.isArray(v.violationTags) ? v.violationTags : [];
                const demo: any = { ...(v.violationDemographics || {}) };
                for (const k of tags) {
                  const entry = { ...(demo[k] || {}) };
                  const c = entry.affectedCount;
                  const isDefault = c == null || (oldVal != null && c === oldVal);
                  if (isDefault) {
                    if (value != null && value !== '') entry.affectedCount = value;
                    else delete entry.affectedCount;
                    demo[k] = entry;
                  }
                }
                updated.violationDemographics = demo;
              }
              return updated;
            }) };
          });
        };
        return (
        <div key={fid} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-gray-400 dark:border-gray-500">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{indigenous ? 'Indigenous group of victims' : 'Non-Indigenous group of victims'}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{indigenous
                ? 'The Indigenous community or people affected together — recorded with headcounts instead of individual names.'
                : 'Non-Indigenous people targeted in the same incident as a group — lawyers, protesters, a support organisation. You can keep them in one entry or add a separate entry for each group, whichever describes the incident better. Recorded alongside the Indigenous victims, never instead of them.'}</p>
            </div>
            {groupVictim ? (
              <button type="button" onClick={() => (indigenous ? removeGroupVictim() : removeVictim(gIdx))} className="text-red-600 text-sm hover:text-red-800 whitespace-nowrap ml-4">Remove</button>
            ) : indigenous ? (
              <button type="button" onClick={() => addGroupVictim(true)} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 whitespace-nowrap ml-4">Add Indigenous group</button>
            ) : (
              <button type="button" onClick={() => addGroupVictim(false)} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 whitespace-nowrap ml-4">Add non-Indigenous group</button>
            )}
          </div>
          {groupVictim ? (
          <div className="space-y-6 mt-4">
            {indigenous ? (
            <TagInput
              tags={formData.indigenousGroup}
              onChange={(tags) => {
                updateFormData('indigenousGroup', tags);
                updateGroupField('groupName', tags);
              }}
              label="Indigenous Group/s *"
              placeholder="Type a group name and press Enter or click +"
              error={stepErrors.indigenousGroup}
            />
            ) : (
            <TagInput
              tags={groupVictim.groupName || []}
              onChange={(tags) => updateGroupField('groupName', tags)}
              label="Name or description"
              placeholder="e.g. the organisation's name, or a more specific description"
              error={stepErrors[`victims[${gIdx}].groupName`]}
            />
            )}

            <div>
              <label htmlFor={`${fid}-kind`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">What kind of group was targeted?{aiMark(`victims[${groupVictim?.id}].groupType`)}</label>
              <select id={`${fid}-kind`} value={(groupVictim as any)?.groupType || ''} onChange={(e) => updateGroupField('groupType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                <option value="">No information</option>
                {sortOptions(VICTIM_GROUP_TYPES.filter(opt => (indigenous ? INDIGENOUS_GROUP_TYPE_VALUES : NON_INDIGENOUS_GROUP_TYPE_VALUES).includes(opt.value) || opt.value === (groupVictim as any)?.groupType)).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
              {selectedDef('groupType', (groupVictim as any)?.groupType || '')}
              {fieldError(`victims[${gIdx}].groupType`)}
              {(groupVictim as any)?.groupType === 'COMMUNITY' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For community targeting, fill in the number of affected communities below.</p>
              )}
            </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Affected People *</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">At least one of these counts is required for a group of victims. Leave the others blank if unknown.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Individuals', field: 'affectedIndividualsCount' },
                      { label: 'Families', field: 'affectedFamiliesCount' },
                      { label: 'Homes', field: 'affectedHomesCount' },
                      { label: 'Groups', field: 'affectedGroupsCount' },
                      { label: 'Communities', field: 'affectedCommunitiesCount' },
                      { label: 'Villages', field: 'affectedVillagesCount' },
                    ].map(({ label, field }) => (
                      <div key={field}>
                        <label htmlFor={`${fid}-${field}`} className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}{aiMark(`victims[${groupVictim?.id}].${field}`)}</label>
                        <input id={`${fid}-${field}`} type="number" min="0" value={(groupVictim as any)?.[field] ?? ''} onChange={(e) => updateGroupField(field, e.target.value ? parseInt(e.target.value) : null)} className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                      </div>
                    ))}
                  </div>
                </div>

                {fieldError(`victims[${gIdx}].groupCount`)}{fieldError(`victims[${gIdx}].affectedCommunitiesCount`)}
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3">Individuals is the total distinct people in this group. Gender and age are recorded per violation, under each violation above.</p>

            <div>
              <label htmlFor={`${fid}-additional-info`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional details{aiMark(`victims[${groupVictim?.id}].additionalInfo`)}</label>
              <RichTextEditor id={`${fid}-additional-info`} value={groupVictim.additionalInfo || ''} onChange={(v) => updateGroupField('additionalInfo', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Anything else about this group the record should hold: who they are, how they were affected, what happened to them since…" />
            </div>

            <div className="border-t pt-6">
              {gIdx === -1 ? null : renderVictimViolationPills(gIdx)}
            </div>
          </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">{indigenous
              ? 'No Indigenous group added yet. Add one if a whole community or people was affected as a collective.'
              : 'Add one only if non-Indigenous people were themselves targeted as a group. You can add more than one if that helps.'}</p>
          )}
        </div>
        );
      })}

    </div>
  );

  // Helper functions for violationDetails array
  const getViolationDetail = (type: string): ViolationDetail => {
    return formData.violationDetails.find(vd => vd.violationType === type) || createEmptyViolationDetail(type);
  };

  const updateViolationDetail = (type: string, field: keyof ViolationDetail, value: any) => {
    setFormData(prev => ({
      ...prev,
      violationDetails: prev.violationDetails.map(vd =>
        vd.violationType === type ? { ...vd, [field]: value } : vd
      )
    }));
  };

  // Checkbox grid for an enum[] field on a violation detail
  const renderViolationEnumChecklist = (type: string, field: keyof ViolationDetail, options: { value: string; label: string }[], listKey?: string) => {
    const vd = getViolationDetail(type);
    const current = (vd[field] as string[]) || [];
    return renderCodePills(listKey || String(field), options, current, (v, next) =>
      updateViolationDetail(type, field, next ? [...current, v] : current.filter(x => x !== v)));
  };

  // Reusable shared violation fields (perpetrator, legal case, state action) per violation type
  // Perpetrator fields — rendered early in each violation section for consistent positioning
  const renderPerpetratorFields = (type: string) => {
    const vd = getViolationDetail(type);
    return (
      <>
        <div>
          <label className="flex items-center mb-3">
            <input type="checkbox" checked={vd.perpetratorKnown} onChange={(e) => updateViolationDetail(type, 'perpetratorKnown', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Are the alleged perpetrators known?</span>
          </label>
        </div>
        {vd.perpetratorKnown && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Perpetrator Types{defsLink('perpetratorTypes')}</label>
              {renderCodePills('perpetratorTypes', PERPETRATOR_TYPES, vd.perpetratorTypes, (v, next) =>
                updateViolationDetail(type, 'perpetratorTypes', next ? [...vd.perpetratorTypes, v] : vd.perpetratorTypes.filter(t => t !== v)), PERPETRATOR_TYPE_GROUPS)}
            </div>
            <div>
              <label htmlFor={`additional-perpetrator-information-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Perpetrator Information{aiMark(`violationDetails[${(vd as any).id}].perpetratorDetails`)}</label>
              <RichTextEditor id={`additional-perpetrator-information-${type}`} value={vd.perpetratorDetails} onChange={(v) => updateViolationDetail(type, 'perpetratorDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          </>
        )}
        <div>
          <label htmlFor={`have-the-alleged-perpetrators-been-held-accountable-for-this-violation-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Have the alleged perpetrators been held accountable for this violation?{aiMark(`violationDetails[${(vd as any).id}].perpetratorAccountabilityStatus`)}</label>
          <select id={`have-the-alleged-perpetrators-been-held-accountable-for-this-violation-${type}`} value={vd.perpetratorAccountabilityStatus} onChange={(e) => updateViolationDetail(type, 'perpetratorAccountabilityStatus', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
            <option value="">No information</option>
            {sortOptions(PERPETRATOR_ACCOUNTABILITY_STATUSES).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
          </select>
          {selectedDef('perpetratorAccountabilityStatus', vd.perpetratorAccountabilityStatus)}
        </div>
      </>
    );
  };

  // Legal case + state action fields — rendered at the end of each violation section
  const renderLegalStateFields = (type: string) => {
    const vd = getViolationDetail(type);
    return (
      <>
        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Were legal cases filed against the perpetrators on behalf of the victim(s)?</label>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === true} onChange={() => updateViolationDetail(type, 'legalCaseFiled', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === false} onChange={() => updateViolationDetail(type, 'legalCaseFiled', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === null} onChange={() => updateViolationDetail(type, 'legalCaseFiled', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
            </label>
          </div>
          {vd.legalCaseFiled === true && (
            <div>
              <label htmlFor={`legal-case-details-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Legal Case Details{aiMark(`violationDetails[${(vd as any).id}].legalCaseDetails`)}</label>
              <RichTextEditor id={`legal-case-details-${type}`} value={vd.legalCaseDetails} onChange={(v) => updateViolationDetail(type, 'legalCaseDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
          {vd.legalCaseFiled === false && (
            <div>
              <label htmlFor={`why-no-legal-case-was-filed-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no legal case was filed?{aiMark(`violationDetails[${(vd as any).id}].legalCaseWhyNot`)}</label>
              <RichTextEditor id={`why-no-legal-case-was-filed-${type}`} value={vd.legalCaseWhyNot} onChange={(v) => updateViolationDetail(type, 'legalCaseWhyNot', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
        </div>

        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Has the State undertaken any actions to address the violation?</label>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === true} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === false} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === null} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
            </label>
          </div>
          {vd.stateActionUndertaken === true && (
            <div>
              <label htmlFor={`state-actions-undertaken-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">State Actions Undertaken{aiMark(`violationDetails[${(vd as any).id}].stateActionDetails`)}</label>
              <RichTextEditor id={`state-actions-undertaken-${type}`} value={vd.stateActionDetails} onChange={(v) => updateViolationDetail(type, 'stateActionDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
          {vd.stateActionUndertaken === false && (
            <div>
              <label htmlFor={`why-no-state-action-${type}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no state action?{aiMark(`violationDetails[${(vd as any).id}].stateActionWhyNot`)}</label>
              <RichTextEditor id={`why-no-state-action-${type}`} value={vd.stateActionWhyNot} onChange={(v) => updateViolationDetail(type, 'stateActionWhyNot', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
        </div>
      </>
    );
  };

  // ============ Categories of HR Violations (rendered as step 4) ============
  const handleOtherViolationToggle = async (value: string) => {
    const isSelected = formData.otherViolationTypes.includes(value);
    const updated = isSelected
      ? formData.otherViolationTypes.filter(t => t !== value)
      : [...formData.otherViolationTypes, value];
    // The shared "other" detail record only goes when the last other type is removed.
    if (isSelected && !(await confirmViolationRemoval(getViolationTypeLabelSafe(value), updated.length === 0 ? 'OTHER' : null, value))) return;

    setFormData(prev => ({
      ...prev,
      otherViolationTypes: updated,
      // Auto-manage 'OTHER' in violationTypes
      violationTypes: updated.length > 0
        ? (prev.violationTypes.includes('OTHER') ? prev.violationTypes : [...prev.violationTypes, 'OTHER'])
        : prev.violationTypes.filter(t => t !== 'OTHER'),
      // Auto-manage violationDetails for OTHER
      violationDetails: updated.length > 0 && !prev.violationDetails.some(vd => vd.violationType === 'OTHER')
        ? [...prev.violationDetails, createEmptyViolationDetail('OTHER')]
        : updated.length === 0
        ? prev.violationDetails.filter(vd => vd.violationType !== 'OTHER')
        : prev.violationDetails,
      // Removing an other-violation prunes it from victims; adding selects it on all by default.
      victims: isSelected
        ? prev.victims.map(v => ({ ...v, violationTags: (v.violationTags || []).filter(t => t !== value) }))
        : prev.victims.map(v => {
            const tags = v.violationTags || [];
            return { ...v, violationTags: tags.includes(value) ? tags : [...tags, value] };
          }),
    }));

    if (stepErrors.violationTypes) {
      setStepErrors(prev => { const next = { ...prev }; delete next.violationTypes; return next; });
    }
    if (stepErrors.otherViolationTypes) {
      setStepErrors(prev => { const next = { ...prev }; delete next.otherViolationTypes; return next; });
    }
  };

  const renderStep5 = () => {
    const crimVd = getViolationDetail('CRIMINALIZATION');
    const killVd = getViolationDetail('KILLING');
    const hasOtherSelected = formData.otherViolationTypes.length > 0;

    const getBadgeStyle = (type: string, isSelected: boolean) =>
      isSelected ? getViolationColor(type).solid : NEUTRAL_BADGE;

    return (
    <div className="space-y-8">
      {/* Violation type selection */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Categories of Incidents of Human Rights Violation</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Select every category the source supports. Details fields appear for each one selected.{defsLink('violationTypes')}</p>
        {listRule('violationTypes') && <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{listRule('violationTypes')}</p>}
        <div className="space-y-4">
          {(VIOLATION_TYPES_TAXONOMY.groups || []).map(group => (
            <div key={group.label}>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{group.label}</h4>
              <div className="flex flex-wrap gap-2">
                {group.values.map(code => {
                  const isMain = code === 'KILLING' || code === 'CRIMINALIZATION';
                  const isSelected = isMain ? formData.violationTypes.includes(code) : formData.otherViolationTypes.includes(code);
                  const label = isMain ? getViolationTypeLabel(code) : (OTHER_VIOLATION_TYPES.find(o => o.value === code)?.label || violationLabel(code));
                  return (
                    <DefinitionHover key={code} listKey="violationTypes" value={code}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => { if (isMain) void handleViolationTypeChange(code, !isSelected); else void handleOtherViolationToggle(code); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${getBadgeStyle(code, isSelected)}`}
                      >
                        {isSelected ? '✓ ' : ''}{label}
                      </button>
                    </DefinitionHover>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {fieldError('violationTypes')}
        {fieldError('otherViolationTypes')}
      </div>

      {/* Codebook pairings — soft reminders in the same style as the FPIC question below */}
      {activePairings.map(pr => (
        <div key={pr.when.join('+') + pr.suggest} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
            {pr.when.map(violationLabel).join(' and ')} usually goes with {violationLabel(pr.suggest)}.
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">{pr.reason}</p>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => addViolation(pr.suggest)} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700">
              + Add {violationLabel(pr.suggest)}
            </button>
            <button type="button" onClick={() => setDismissedPairings(prev => new Set(prev).add(pr.when.join('+') + '>' + pr.suggest))} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:underline">
              Not in this case
            </button>
          </div>
        </div>
      ))}

      {/* Killing Details */}
      {formData.violationTypes.includes('KILLING') && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-red-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Killing Details</h2>
          <div className="space-y-6">
            <div>
              <label htmlFor="why-was-were-the-victim-s-killed" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why was/were the victim(s) killed?{aiMark(`violationDetails[${(killVd as any).id}].whyVictimsKilled`)}</label>
              <RichTextEditor id="why-was-were-the-victim-s-killed" value={killVd.whyVictimsKilled} onChange={(v) => updateViolationDetail('KILLING', 'whyVictimsKilled', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
            <div>
              <label htmlFor="how-was-the-killing-carried-out" className="block text-sm font-medium text-gray-700 dark:text-gray-300">How was the killing carried out?{aiMark(`violationDetails[${(killVd as any).id}].howKillingCarriedOut`)}</label>
              <RichTextEditor id="how-was-the-killing-carried-out" value={killVd.howKillingCarriedOut} onChange={(v) => updateViolationDetail('KILLING', 'howKillingCarriedOut', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Did the victim(s) experience harassment or intimidation prior to the killing?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === true} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === false} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === null} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {killVd.harassmentPrior === true && (
              <div>
                <label htmlFor="harassment-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Harassment Details{aiMark(`violationDetails[${(killVd as any).id}].harassmentPriorDetails`)}</label>
                <RichTextEditor id="harassment-details" value={killVd.harassmentPriorDetails} onChange={(v) => updateViolationDetail('KILLING', 'harassmentPriorDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            {renderPerpetratorFields('KILLING')}
            {renderLegalStateFields('KILLING')}
          </div>
        </div>
      )}

      {/* Criminalization Details */}
      {formData.violationTypes.includes('CRIMINALIZATION') && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-orange-500">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Criminalization Details</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Form of criminalization{defsLink('criminalizationTypes')}</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{listRule('criminalizationTypes')}</p>
              {renderViolationEnumChecklist('CRIMINALIZATION', 'criminalizationTypes', CRIMINALIZATION_TYPES, 'criminalizationTypes')}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Why was/were the victim(s) criminalized?{defsLink('criminalizationTriggers')}</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{listRule('criminalizationTriggers')}</p>
              {renderViolationEnumChecklist('CRIMINALIZATION', 'criminalizationTriggers', CRIMINALIZATION_TRIGGERS, 'criminalizationTriggers')}
              <RichTextEditor value={crimVd.whyVictimsCriminalized} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'whyVictimsCriminalized', v)} rows={3} className="mt-3 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the circumstances in your own words..." />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Did the victim(s) experience harassment or intimidation prior to criminalization?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.harassmentPrior === true && (
              <div>
                <label htmlFor="harassment-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Harassment Details{aiMark(`violationDetails[${(crimVd as any).id}].harassmentPriorDetails`)}</label>
                <RichTextEditor id="harassment-details" value={crimVd.harassmentPriorDetails} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'harassmentPriorDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            <div>
              <label htmlFor="what-were-the-accusations-or-charges-filed" className="block text-sm font-medium text-gray-700 dark:text-gray-300">What were the accusations or charges filed?{aiMark(`violationDetails[${(crimVd as any).id}].accusations`)}</label>
              <RichTextEditor id="what-were-the-accusations-or-charges-filed" value={crimVd.accusations} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'accusations', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label htmlFor="policies-or-laws-used-against-the-victim-s" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Policies or laws used against the victim(s){aiMark(`violationDetails[${(crimVd as any).id}].policiesOrLawsUsed`)}</label>
              <RichTextEditor id="policies-or-laws-used-against-the-victim-s" value={crimVd.policiesOrLawsUsed} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'policiesOrLawsUsed', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categories of legal instruments used{defsLink('legalInstrumentTypes')}</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{listRule('legalInstrumentTypes')}</p>
              {renderViolationEnumChecklist('CRIMINALIZATION', 'legalInstrumentTypes', LEGAL_INSTRUMENT_TYPES, 'legalInstrumentTypes')}
            </div>

            <div>
              <label htmlFor="type-of-proceeding" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type of proceeding{aiMark(`violationDetails[${(crimVd as any).id}].proceedingType`)}</label>
              <select id="type-of-proceeding" value={crimVd.proceedingType} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'proceedingType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                <option value="">No information</option>
                {sortOptions(PROCEEDING_TYPES).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
              {selectedDef('proceedingType', crimVd.proceedingType)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Is this a retaliatory proceeding (e.g., a countersuit or SLAPP-style filing after a community complaint)?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-retaliatory" checked={crimVd.isRetaliatoryProceeding === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'isRetaliatoryProceeding', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-retaliatory" checked={crimVd.isRetaliatoryProceeding === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'isRetaliatoryProceeding', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-retaliatory" checked={crimVd.isRetaliatoryProceeding === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'isRetaliatoryProceeding', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Who filed the accusations or charges?{defsLink('perpetratorTypes')}</label>
              {renderCodePills('perpetratorTypes', PERPETRATOR_TYPES, crimVd.whoFiledAccusations, (v, next) =>
                updateViolationDetail('CRIMINALIZATION', 'whoFiledAccusations', next ? [...crimVd.whoFiledAccusations, v] : crimVd.whoFiledAccusations.filter(t => t !== v)), PERPETRATOR_TYPE_GROUPS)}
            </div>
            <div>
              <label htmlFor="additional-details-about-who-filed" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional details about who filed{aiMark(`violationDetails[${(crimVd as any).id}].whoFiledAccusationsDetails`)}</label>
              <RichTextEditor id="additional-details-about-who-filed" value={crimVd.whoFiledAccusationsDetails} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'whoFiledAccusationsDetails', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Was/were the accusation/s or charge/s against the victim legally or formally filed?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.accusationsLegallyFiled === true && (
              <div>
                <label htmlFor="where-was-the-case-filed" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Where was the case filed?{aiMark(`violationDetails[${(crimVd as any).id}].courtFilingLocation`)}</label>
                <input id="where-was-the-case-filed" type="text" value={crimVd.courtFilingLocation} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'courtFilingLocation', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Court or jurisdiction..." />
              </div>
            )}
            {(crimVd.accusationsLegallyFiled === true || crimVd.accusationsLegallyFiled === false) && (
              <div>
                <label htmlFor="provide-more-information" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provide more information{aiMark(`violationDetails[${(crimVd as any).id}].courtFilingDetails`)}</label>
                <RichTextEditor id="provide-more-information" value={crimVd.courtFilingDetails} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'courtFilingDetails', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Is there a decision on the legal accusations or charges?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.decisionOnAccusations === true && (
              <div>
                <label htmlFor="decision-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Decision Details{aiMark(`violationDetails[${(crimVd as any).id}].decisionDetails`)}</label>
                <RichTextEditor id="decision-details" value={crimVd.decisionDetails} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'decisionDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}
            {crimVd.decisionOnAccusations === false && (
              <div>
                <label htmlFor="please-provide-more-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Please provide more details{aiMark(`violationDetails[${(crimVd as any).id}].decisionDetails`)}</label>
                <RichTextEditor id="please-provide-more-details" value={crimVd.decisionDetails} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'decisionDetails', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Why is there no decision yet?" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Is/Are the victim(s) in prison or detention?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.victimsInPrison === true && (
              <div>
                <label htmlFor="prison-or-detention-conditions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prison or Detention Conditions{aiMark(`violationDetails[${(crimVd as any).id}].prisonConditions`)}</label>
                <RichTextEditor id="prison-or-detention-conditions" value={crimVd.prisonConditions} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'prisonConditions', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}
            {crimVd.victimsInPrison === false && (
              <div>
                <label htmlFor="current-situation-of-victim-s" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Situation of Victim(s){aiMark(`violationDetails[${(crimVd as any).id}].victimsSituationIfNotInPrison`)}</label>
                <RichTextEditor id="current-situation-of-victim-s" value={crimVd.victimsSituationIfNotInPrison} onChange={(v) => updateViolationDetail('CRIMINALIZATION', 'victimsSituationIfNotInPrison', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="If not in prison, describe their current situation..." />
              </div>
            )}

            {renderPerpetratorFields('CRIMINALIZATION')}
            {renderLegalStateFields('CRIMINALIZATION')}
          </div>
        </div>
      )}

      {/* Other HRV Details — shown when any non-Killing/Criminalization type is selected */}
      {hasOtherSelected && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-emerald-500">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Other Human Rights Violation Details</h2>
          <div className="flex flex-wrap gap-1 mb-6">
            {formData.otherViolationTypes.map(v => {
              const opt = OTHER_VIOLATION_TYPES.find(o => o.value === v);
              return opt ? <span key={v} className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">{opt.label}</span> : null;
            })}
          </div>
          <div className="space-y-6">
            {/* If "Other" specifically is selected, show free-form text */}
            {formData.otherViolationTypes.includes('OTHER') && (
              <div>
                <label htmlFor="please-specify-the-other-violation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Please specify the other violation{aiMark('otherViolationTypeDetails')}</label>
                <RichTextEditor id="please-specify-the-other-violation"
                  value={formData.otherViolationTypeDetails}
                  onChange={(v) => updateFormData('otherViolationTypeDetails', v)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Describe the violation type..."
                />
{fieldError('otherViolationTypeDetails')}
              </div>
            )}

            <div>
              <label htmlFor="what-caused-the-human-rights-violation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">What caused the human rights violation?{aiMark(`violationDetails[${(getViolationDetail('OTHER') as any).id}].cause`)}</label>
              <RichTextEditor id="what-caused-the-human-rights-violation" value={getViolationDetail('OTHER').cause} onChange={(v) => updateViolationDetail('OTHER', 'cause', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            {renderPerpetratorFields('OTHER')}
            {renderLegalStateFields('OTHER')}
          </div>
        </div>
      )}

      {/* FPIC Violation inline question */}
      {formData.otherViolationTypes.some(t => FPIC_RELATED_TYPES.includes(t)) &&
       !formData.otherViolationTypes.includes('FPIC_VIOLATION') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800 mb-3">
            Is this related to a violation of Free, Prior and Informed Consent (FPIC)?
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleOtherViolationToggle('FPIC_VIOLATION')}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
            >
              Yes
            </button>
            <span className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
              If not, you can continue without selecting it.
            </span>
          </div>
        </div>
      )}

      {/* FPIC companion reminder — the mirror of the question above: FPIC is tagged but none of the
          violations that usually accompany it are. Purely a prompt to re-check the sources; it never
          blocks submission, and the encoder can continue without selecting anything. */}
      {formData.otherViolationTypes.includes('FPIC_VIOLATION') &&
       !formData.otherViolationTypes.some(t => FPIC_RELATED_TYPES.includes(t)) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
            Before continuing — does this case also involve any of these?
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            A violation of Free, Prior and Informed Consent is often accompanied by land seizure, displacement, or pressure on the
            community. Please re-check the sources. None of these is required: if none applies, carry on.
          </p>
          <div className="flex flex-wrap gap-2">
            {FPIC_RELATED_TYPES.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => handleOtherViolationToggle(type)}
                className="px-3 py-1.5 rounded-full text-xs font-medium border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
              >
                + {OTHER_VIOLATION_TYPES.find(o => o.value === type)?.label ?? getViolationTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    );
  };

  // ============ Impact of HR Violations (rendered as step 7) ============
  const renderStep6 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Impact of Incidents of Human Rights Violations</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Types of impact{defsLink('impactTypes')}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{IMPACT_TYPES_TAXONOMY.rule}</p>
          {renderCodePills('impactTypes', IMPACT_TYPES, formData.impactTypes, (v, next) =>
            updateFormData('impactTypes', next ? [...formData.impactTypes, v] : formData.impactTypes.filter(x => x !== v)))}
        </div>

        <div>
          <label htmlFor="how-did-the-incident-s-affect-the-victim-s-and-their-family" className="block text-sm font-medium text-gray-700 dark:text-gray-300">How did the incident(s) affect the victim(s) and their family?{aiMark('familyImpact')}</label>
          <RichTextEditor id="how-did-the-incident-s-affect-the-victim-s-and-their-family" value={formData.familyImpact} onChange={(v) => updateFormData('familyImpact', v)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the impact on family members..." />
        </div>

        <div>
          <label htmlFor="how-did-the-incident-s-affect-the-indigenous-peoples-group-s-or-community-ies" className="block text-sm font-medium text-gray-700 dark:text-gray-300">How did the incident(s) affect the Indigenous Peoples group(s) or community/ies?{aiMark('communityImpact')}</label>
          <RichTextEditor id="how-did-the-incident-s-affect-the-indigenous-peoples-group-s-or-community-ies" value={formData.communityImpact} onChange={(v) => updateFormData('communityImpact', v)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the impact on the community..." />
        </div>

      </div>
    </div>
  );

  // ============ STEP 8: Response and Follow-up ============
  const renderStepResponse = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Response and Follow-up</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">What happened after the incident: whether the authorities investigated, and whether the community, IPRI or others took action.</p>
      <div className="space-y-6">
        <div>
          {triState('investigationConducted', 'Has there been an investigation into the incident?')}
          {formData.investigationConducted === true ? (
            <div>
              <label htmlFor="investigation-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Investigation Details{aiMark('investigationDetails')}</label>
              <RichTextEditor id="investigation-details" value={formData.investigationDetails} onChange={(v) => updateFormData('investigationDetails', v)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          ) : formData.investigationConducted === false ? (
            <div>
              <label htmlFor="why-no-investigation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no investigation?{aiMark('investigationWhyNot')}</label>
              <RichTextEditor id="why-no-investigation" value={formData.investigationWhyNot} onChange={(v) => updateFormData('investigationWhyNot', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          ) : null}
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          {triState('advocacyUndertaken', 'Has any advocacy or other action been undertaken?')}
        </div>
        {formData.advocacyUndertaken === true ? (
          <div>
            <label htmlFor="advocacy-actions-and-details" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Advocacy Actions and Details{aiMark('advocacyDetails')}</label>
            <RichTextEditor id="advocacy-actions-and-details" value={formData.advocacyDetails} onChange={(v) => updateFormData('advocacyDetails', v)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe advocacy efforts, campaigns, legal actions..." />
          </div>
        ) : formData.advocacyUndertaken === false ? (
          <div>
            <label htmlFor="why-no-advocacy-undertaken" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no advocacy undertaken?{aiMark('advocacyWhyNot')}</label>
            <RichTextEditor id="why-no-advocacy-undertaken" value={formData.advocacyWhyNot} onChange={(v) => updateFormData('advocacyWhyNot', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
          </div>
        ) : null}
      </div>
    </div>
  );

  // ============ STEP 5: Perpetrator Details (named perpetrators + related development project) ============
  const renderStepPerpetrators = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Who was responsible?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Name the people or organisations alleged to be responsible, and record any development project the case is tied to. The <em>kinds</em> of perpetrator are already recorded per violation on the Categories step.</p>
      </div>

      {/* Perpetrators */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Perpetrators</h2>
          <button type="button" onClick={addPerpetrator} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
            Add Perpetrator
          </button>
        </div>
        <div className="space-y-6">
          {formData.perpetrators.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg px-4 py-6 text-center">
              No perpetrators named yet. Add a person or an organisation when the source names one.</p>
          )}
          {formData.perpetrators.map((perpetrator, index) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-sm font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">Perpetrator {index + 1}</span>
                <div className="flex items-center gap-4">
                  <span role="radiogroup" aria-label="Kind of perpetrator" className="inline-flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <label className="inline-flex items-center gap-1.5"><input type="radio" name={`perp-kind-${index}`} checked={perpetrator.isOrganization} onChange={() => updatePerpetrator(index, 'isOrganization', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600" />Organisation</label>
                    <label className="inline-flex items-center gap-1.5"><input type="radio" name={`perp-kind-${index}`} checked={!perpetrator.isOrganization} onChange={() => updatePerpetrator(index, 'isOrganization', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600" />Individual</label>
                  </span>
                  <button type="button" onClick={() => removePerpetrator(index)} className="text-red-600 text-sm hover:text-red-800">Remove</button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`perp-name-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">{perpetrator.isOrganization ? 'Organisation name' : 'Person name'} *</label>
                  <input id={`perp-name-${index}`} {...errorProps(`perpetrators[${index}].name`)} type="text" value={perpetrator.name} onChange={(e) => updatePerpetrator(index, 'name', e.target.value)} className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass(`perpetrators[${index}].name`)}`} />
                  {fieldError(`perpetrators[${index}].name`)}
                </div>
                <div>
                  <label htmlFor={`type-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                  <select id={`type-${index}`} value={perpetrator.type || ''} onChange={(e) => updatePerpetrator(index, 'type', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Not recorded</option>
                    {PERPETRATOR_TYPE_GROUPS.map(g => (
                      <optgroup key={g.label} label={g.label}>
                        {g.types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`affiliation-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Affiliation</label>
                  <input id={`affiliation-${index}`} type="text" value={perpetrator.affiliation || ''} onChange={(e) => updatePerpetrator(index, 'affiliation', e.target.value)} placeholder={perpetrator.isOrganization ? 'Parent body, unit, contractor of…' : 'Unit, company or group they belong to'} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                {perpetrator.isOrganization ? (
                  <div>
                    <label htmlFor={`org-head-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Head of organisation</label>
                    <input id={`org-head-${index}`} type="text" value={perpetrator.organizationHead || ''} onChange={(e) => updatePerpetrator(index, 'organizationHead', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                  </div>
                ) : (
                  <div>
                    <label htmlFor={`position-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Position or rank</label>
                    <input id={`position-${index}`} type="text" value={perpetrator.position || ''} onChange={(e) => updatePerpetrator(index, 'position', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                  </div>
                )}
              </div>
              <div>
                <label htmlFor={`notes-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <RichTextEditor id={`notes-${index}`} value={perpetrator.notes || ''} onChange={(v) => updatePerpetrator(index, 'notes', v)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Related Development Projects</h2>
        <div className="space-y-6">
          <div>
            {triState('isRelatedToDevelopmentProject', 'Is this case related to a development project?')}
          </div>

          {formData.isRelatedToDevelopmentProject === true && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                One entry per project. When the case involves several (a dam and its transmission line, logging and mining on the same territory), add each one; the first is the primary project used in summaries and on the map.
              </p>
              {listRule('developmentProjectType') && <p className="text-xs text-gray-500 dark:text-gray-400">{listRule('developmentProjectType')}</p>}
              {formData.developmentProjects.map((project, index) => {
                const key = project.id || String(index);
                // AI ledger entries address a saved row by id; an unsaved row only has its position.
                const path = (f: string) => (project.id ? [`developmentProjects[${project.id}].${f}`, `developmentProjects[${index}].${f}`] : `developmentProjects[${index}].${f}`);
                const subtypeOptions = developmentProjectSubtypesFor(project.type).map(e => ({ value: e.value, label: e.label }));
                return (
                  <div key={key} id={`development-project-${key}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <span className="text-sm font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">{index === 0 ? 'Primary project' : `Project ${index + 1}`}</span>
                      <button type="button" onClick={() => removeProject(index)} className="text-red-600 text-sm hover:text-red-800">Remove</button>
                    </div>
                    <div>
                      <label htmlFor={`project-type-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type of Development Project{aiMark(path('type'))}</label>
                      <select id={`project-type-${key}`} value={project.type} onChange={(e) => updateProject(index, { type: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                        <option value="">Select project type...</option>
                        {sortOptions(DEVELOPMENT_PROJECT_TYPES).map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                      </select>
                      {selectedDef('developmentProjectType', project.type)}
                    </div>
                    {subtypeOptions.length > 0 && (
                      <div>
                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project subtypes{aiMark(path('subtypes'))}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Select every kind that applies.</p>
                        {renderCodePills('developmentProjectSubtype', subtypeOptions, project.subtypes, (value, next) => toggleProjectSubtype(index, value, next))}
                      </div>
                    )}
                    <div>
                      <label htmlFor={`project-name-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name of Development Project{aiMark(path('name'))}</label>
                      <input id={`project-name-${key}`} type="text" value={project.name} onChange={(e) => updateProject(index, { name: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., Belo Monte Dam, Trans-Amazonian Highway..." />
                    </div>
                    <div>
                      <label htmlFor={`project-description-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description of Development Project{aiMark(path('description'))}</label>
                      <RichTextEditor id={`project-description-${key}`} value={project.description} onChange={(v) => updateProject(index, { description: v })} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the project, its scope, and impact..." />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor={`project-start-year-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Start Year{aiMark(path('startYear'))}</label>
                        <input id={`project-start-year-${key}`} type="number" min="1900" max="2100" value={project.startYear} onChange={(e) => updateProject(index, { startYear: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., 2015" />
                      </div>
                      <div>
                        <label htmlFor={`project-website-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Website/Link{aiMark(path('websiteLink'))}</label>
                        <input id={`project-website-${key}`} type="url" value={project.websiteLink} onChange={(e) => updateProject(index, { websiteLink: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="https://..." />
                      </div>
                    </div>
                    <div>
                      <label htmlFor={`project-source-${key}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source of Information on Project{aiMark(path('source'))}</label>
                      <RichTextEditor id={`project-source-${key}`} value={project.source} onChange={(v) => updateProject(index, { source: v })} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                    </div>
                  </div>
                );
              })}
              <button type="button" onClick={addProject} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
                {formData.developmentProjects.length === 0 ? 'Add a project' : 'Add another project'}
              </button>
            </>
          )}
        </div>
      </div>

    </div>
  );

  // ============ STEP 9: Additional Information ============
  const renderStep8 = () => (
    <div className="space-y-8">
      {/* Documents */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Upload Documents</h2>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Upload evidence, witness statements, legal documents, photos, or any other supporting materials for this case.
          </p>

          {existingDocuments}

          <FileUpload onFilesSelected={handleFilesSelected} maxFiles={10} maxFileSize={50} />
          {Object.entries(stepErrors).filter(([k]) => k.startsWith('doc_')).map(([k, m]) => (
            <p key={k} id={`err-${k}`} role="alert" className="text-sm text-red-600">{m}</p>
          ))}
          {formData.files.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                {formData.files.length} file(s) ready to upload
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <TagInput
          tags={formData.tags}
          onChange={(tags) => updateFormData('tags', tags)}
          label="Tags"
          placeholder="Type a tag and press Enter or click +"
          suggestions={SUGGESTED_CASE_TAGS}
        />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep5(); // Categories of HR Violations
      case 5: return renderStepPerpetrators(); // Perpetrator Details
      case 6: return renderStep4(); // Victim Details (picks from the selected violations)
      case 7: return renderStep6(); // Impact
      case 8: return renderStepResponse();
      case 9: return renderStep8(); // Additional Information
      default: return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={(e) => {
      // Enter must not submit from a single-line control, but the multi-line ones (textareas and
      // the rich-text editors, which are contentEditable divs) need it for new lines.
      const t = e.target as HTMLElement;
      if (e.key === 'Enter' && t.tagName !== 'TEXTAREA' && !t.isContentEditable) e.preventDefault();
    }}>
      {renderStepper()}
      {renderErrorSummary()}

      {renderCurrentStep()}

      {/* Action bar: stays in view at the bottom so a long step never hides the buttons */}
      <div className="sticky bottom-0 z-10 mt-8 -mx-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur flex flex-wrap items-center gap-y-3">
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`px-6 py-3 rounded-md font-medium ${
            currentStep === 1
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          Back
        </button>
        {currentStep < TOTAL_STEPS && (
          <button
            type="button"
            onClick={handleNext}
            className="ml-3 px-6 py-3 bg-white dark:bg-gray-800 text-emerald-600 border border-emerald-600 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/30 font-medium"
          >
            Next
          </button>
        )}
        <button
          type="button"
          onClick={() => { void handleCancel(); }}
          className="ml-3 px-4 py-3 rounded-md font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <div className="flex-1" />

        <div className="flex items-center gap-3">
          {renderAutosave()}
          {Object.keys(stepErrors).length > 0 && (
            <span className="text-sm font-medium text-red-600 mr-1">{Object.keys(stepErrors).length} problem{Object.keys(stepErrors).length === 1 ? '' : 's'} to fix</span>
          )}
          {isDraftEditable && (
            <button
              type="button"
              disabled={loading}
              onClick={() => { void attemptSubmit(true, runDocValidation()); }}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Saving...' : (isEditing && caseStatus === 'DRAFT' ? 'Save Changes' : 'Save as Draft')}
            </button>
          )}

          <button
            type="button"
            disabled={loading}
            onClick={() => { void attemptSubmit(false, runValidation()); }}
            className="px-6 py-3 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? (isEditing ? 'Saving...' : 'Submitting...') : (isEditing && caseStatus !== 'DRAFT' ? 'Save Changes' : 'Submit Case')}
          </button>
        </div>
      </div>
          <GlossaryDrawer listKey={glossary} onClose={() => setGlossary(null)} selected={glossary === 'violationTypes' ? [...formData.violationTypes, ...formData.otherViolationTypes] : glossary === 'impactTypes' ? formData.impactTypes : undefined} />
    </form>
  );
};

export default CaseFormWizard;
